import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { useReducedMotion } from "framer-motion"
import { usePage } from "@inertiajs/react"
import { Loader2, Pencil, RotateCcw, Send, Sparkles, Square, X } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/primitives"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { ConfirmActionDialog } from "@/components/confirm-action-dialog"
import { AgentMarkdown } from "@/components/agent/agent-markdown"
import {
  AgentImageAttach,
  AgentPendingImageChips,
  appendPendingAgentImages,
  pendingAgentImagesPayload,
  pendingAgentQueueLabel,
  releasePendingAgentImage,
  type PendingAgentImage,
} from "@/components/agent/agent-image-attach"
import { AgentMessageImages } from "@/components/agent/agent-message-images"
import { AgentComposerEditor, type AgentComposerEditorHandle } from "@/components/agent/agent-composer-editor"
import { AgentNodeMentionText } from "@/components/agent/agent-node-mention-text"
import { AgentSessionSkillsPicker } from "@/components/agent/agent-session-skills"
import { TaskFileDropOverlay } from "@/components/tasks/task-file-drop-overlay"
import { VoiceDictationButton } from "@/components/voice/voice-dictation-button"
import { formatElapsed, JobElapsedTimer } from "@/components/jobs/job-elapsed-timer"
import { EntityThumbSquare } from "@/components/workspace/entity-thumb-square"
import { useAgentNetworkAutoRetry } from "@/hooks/use-agent-network-auto-retry"
import {
  cancelAgentAction,
  cancelAgentRun,
  confirmAgentAction,
  fetchAgentHistory,
  fetchAgentRunState,
  postAgentChat,
  postAgentEditAndResend,
  type AgentApiScope,
  type AgentConfirmActionResult,
  type AgentContextType,
  type AgentCanvasDraftUpdated,
  type AgentEntityUpdated,
  type AgentHistoryResponse,
  type AgentMessageImage,
  type AgentMessageRow,
  type AgentPendingAction,
  type AgentRunState,
  type AgentRunStateResponse,
  type AgentFlowNodePromptUpdated,
  type AgentRunLastEvents,
  type AgentSessionSkill,
  type AgentShotCharactersUpdated,
  type AgentShotCreated,
  type AgentShotDurationUpdated,
  type AgentShotVideoReferencesUpdated,
  type AgentShotLocationUpdated,
  type AgentShotSceneUpdated,
  type AgentShotPromptUpdated,
  type AgentShotsBatchQueued,
  type AgentSceneCreated,
  type Scene3dAgentFocus,
  type Scene3dLayoutUpdated,
} from "@/lib/agent-api"
import { AUTO_SEND_MAX_ATTEMPTS, decideAutoSendAfterAttempt } from "@/lib/agent-auto-send"
import {
  mergeHistoryMessages,
  oldestPositiveId,
  revokeBlobPreviewUrls,
  withLocalPreviewUrls,
} from "@/lib/agent-message-preview"
import { clipboardHasFiles, filesFromClipboard, normalizePastedFiles } from "@/lib/clipboard-files"
import { getCableConsumer, subscribeCableChannel } from "@/lib/cable-consumer"
import { nextRunStateBackoffMs, RUN_STATE_FALLBACK_MS, runStatePollDelayMs } from "@/lib/agent-run-state-poll"
import {
  awaitingClaimWatch,
  classifyRunSnapshot,
  isRunActive,
  watchAfterActiveRun,
  type RunWatch,
} from "@/lib/agent-run-watch"
import { AgentHandoffChip } from "@/components/agent/agent-handoff-chip"
import { notifyNotesChanged } from "@/lib/notes-api"
import { AgentSavedPromptBlock, parseSavedPromptCards } from "@/components/agent/agent-saved-prompt-block"
import type { MentionItem } from "@/components/prompt-editor/types"
import { agentImageMentionItems, remapAgentImageTokens } from "@/lib/agent-image-mentions"
import { flowNodeMentionIdsFromText } from "@/lib/flow-node-mentions"
import { parseHandoffMeta } from "@/lib/agent-handoff"
import { ensureScene3dPosterIfMissing } from "@/lib/location-scene-3d/capture-scene-poster"
import { personDisplayName } from "@/lib/person-name"
import { chatAgentLabelKey } from "@/lib/task-agents"
import { cn } from "@/lib/utils"
import { useSidebarCollapsed } from "@/stores/ui-store"
import { useJobKindFinished } from "@/hooks/use-job-kind-finished"
import { useAgentComposerDraft } from "@/hooks/use-agent-composer-draft"
import { useAgentComposerImages } from "@/hooks/use-agent-composer-images"
import { useTranslation } from "@/hooks/use-translation"
import { useRotatingPhrase } from "@/hooks/use-rotating-phrase"
import { useShellDrawerContentRight } from "@/hooks/use-shell-drawer-content-right"
import { useSwapHeightAnimation } from "@/hooks/use-swap-height-animation"
import type { AgentShotRef } from "@/lib/shot-label"

const PANEL_CARD_CLASS =
  "relative overflow-visible rounded-lg border border-ui-border p-3 shadow-[0_-8px_40px_-8px_rgba(0,0,0,0.45)] ring-1 ring-black/5 backdrop-blur-md dark:bg-zinc-950/80 dark:shadow-2xl dark:shadow-black/40 light:bg-ui-bg-elevated/88 light:shadow-[0_-4px_24px_rgba(0,0,0,0.12)]"

const BUSY_RETRY_MS = 700
const BUSY_RETRY_BUDGET_MS = 10_000
const HISTORY_FADE_MS = 120

function sleep(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })
}

function isAbortError(error: unknown): boolean {
  return (error instanceof DOMException || error instanceof Error) && error.name === "AbortError"
}

function formatAgentMessageStamp(iso: string | undefined, locale: string): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString(locale === "ru" ? "ru-RU" : "en-US", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  })
}

/** Blink cursor at end of last markdown block while streaming. */
const STREAM_CURSOR_CLASS =
  "[&>*:last-child:not(ol):not(ul)]:after:ml-0.5 [&>*:last-child:not(ol):not(ul)]:after:inline-block " +
  "[&>*:last-child:not(ol):not(ul)]:after:animate-pulse [&>*:last-child:not(ol):not(ul)]:after:content-['▍'] " +
  "[&>:is(ol,ul):last-child>li:last-child]:after:ml-0.5 [&>:is(ol,ul):last-child>li:last-child]:after:inline-block " +
  "[&>:is(ol,ul):last-child>li:last-child]:after:animate-pulse [&>:is(ol,ul):last-child>li:last-child]:after:content-['▍']"

const QUICK_COMMANDS_SHOT = [
  { key: "improve", phraseKey: "agent.quickImprove" },
  { key: "keyframe", phraseKey: "agent.quickKeyframe" },
  { key: "video", phraseKey: "agent.quickVideo" },
  { key: "nextShot", phraseKey: "agent.quickNextShot" },
] as const

const QUICK_COMMANDS_STORYBOARD = [{ key: "nextShotByScript", phraseKey: "agent.quickNextShotByScript" }] as const

const QUICK_COMMANDS_CHARACTERS = [
  { key: "createCharacter", phraseKey: "agent.quickCreateCharacter" },
  { key: "updateCharacterProfile", phraseKey: "agent.quickUpdateCharacterProfile" },
  {
    key: "extractCharacters",
    phraseKey: "agent.quickExtractCharacters",
    labelKey: "agent.quickExtractCharactersLabel",
  },
  { key: "generateCharacterReference", phraseKey: "agent.quickGenerateCharacterReference" },
  { key: "generateCharacterSheet", phraseKey: "agent.quickGenerateCharacterSheet" },
  { key: "generateCharacterEmotionSheet", phraseKey: "agent.quickGenerateCharacterEmotionSheet" },
  { key: "generateCharacterPortrait", phraseKey: "agent.quickGenerateCharacterPortrait" },
  { key: "generateCharacterScalePose", phraseKey: "agent.quickGenerateCharacterScalePose" },
  { key: "generateCharacterMedia", phraseKey: "agent.quickGenerateCharacterMedia" },
  { key: "registerSeedanceAvatar", phraseKey: "agent.quickRegisterSeedanceAvatar" },
] as const

const QUICK_COMMANDS_LOCATIONS = [
  { key: "createLocation", phraseKey: "agent.quickCreateLocation" },
  { key: "updateLocationProfile", phraseKey: "agent.quickUpdateLocationProfile" },
  {
    key: "extractLocations",
    phraseKey: "agent.quickExtractLocations",
    labelKey: "agent.quickExtractLocationsLabel",
  },
  { key: "generateLocationReference", phraseKey: "agent.quickGenerateLocationReference" },
] as const

const QUICK_COMMANDS_SCENES3D = [
  { key: "createScene3d", phraseKey: "agent.quickCreateScene3d" },
  { key: "describeScene3d", phraseKey: "agent.quickDescribeScene3d" },
  { key: "placeScene3dHero", phraseKey: "agent.quickPlaceScene3dHero" },
  { key: "paintScene3dFace", phraseKey: "agent.quickPaintScene3dFace" },
] as const

const QUICK_COMMANDS_PROPS = [
  { key: "createProp", phraseKey: "agent.quickCreateProp" },
  { key: "updatePropProfile", phraseKey: "agent.quickUpdatePropProfile" },
  { key: "extractProps", phraseKey: "agent.quickExtractProps" },
  { key: "generatePropReference", phraseKey: "agent.quickGeneratePropReference" },
  { key: "generatePropFrontView", phraseKey: "agent.quickGeneratePropFrontView" },
] as const

const QUICK_COMMANDS_CANVAS = [
  { key: "canvasImprove", phraseKey: "agent.quickCanvasImprove" },
  { key: "canvasVideo", phraseKey: "agent.quickCanvasVideo" },
  { key: "canvasGenerate", phraseKey: "agent.quickCanvasGenerate" },
] as const

const QUICK_COMMANDS_SKILLS = [
  { key: "updateSkillBody", phraseKey: "agent.quickUpdateSkillBody" },
  { key: "simplifySkill", phraseKey: "agent.quickSimplifySkill" },
  { key: "clarifySkill", phraseKey: "agent.quickClarifySkill" },
] as const

const QUICK_COMMANDS_SKILLS_DISCUSS = [
  { key: "explainSkill", phraseKey: "agent.quickExplainSkill" },
  { key: "findSkillContradictions", phraseKey: "agent.quickFindSkillContradictions" },
] as const

const QUICK_COMMANDS_SCRIPT = [
  {
    key: "extractCharacters",
    phraseKey: "agent.quickExtractCharacters",
    labelKey: "agent.quickExtractCharactersFromScriptLabel",
  },
  {
    key: "extractLocations",
    phraseKey: "agent.quickExtractLocations",
    labelKey: "agent.quickExtractLocationsFromScriptLabel",
  },
  { key: "extractProps", phraseKey: "agent.quickExtractProps" },
  { key: "shotsFromScript", phraseKey: "agent.quickShotsFromScript" },
  {
    key: "scriptFromSynopsis",
    phraseKey: "agent.quickScriptFromSynopsis",
    labelKey: "agent.quickScriptFromSynopsisLabel",
  },
  {
    key: "fitScriptDuration",
    phraseKey: "agent.quickFitScriptDuration",
    labelKey: "agent.quickFitScriptDurationLabel",
  },
  { key: "summarizeScript", phraseKey: "agent.quickSummarizeScript" },
] as const

const QUICK_COMMANDS_PROJECTS_HOME = [
  { key: "listProjects", phraseKey: "agent.quickListProjects" },
  { key: "companyBalance", phraseKey: "agent.quickCompanyBalance" },
  { key: "explainSections", phraseKey: "agent.quickExplainSections" },
  { key: "createProject", phraseKey: "agent.quickCreateProject", labelKey: "agent.quickCreateProjectLabel" },
] as const

type AgentQuickSurface =
  | "shot"
  | "storyboard"
  | "project"
  | "characters"
  | "locations"
  | "props"
  | "canvas"
  | "skills"
  | "skills_discuss"
  | "script"
  | "projects_home"
  | "scenes3d"
type AgentQuickCommand =
  | (typeof QUICK_COMMANDS_SHOT)[number]
  | (typeof QUICK_COMMANDS_STORYBOARD)[number]
  | (typeof QUICK_COMMANDS_CHARACTERS)[number]
  | (typeof QUICK_COMMANDS_LOCATIONS)[number]
  | (typeof QUICK_COMMANDS_PROPS)[number]
  | (typeof QUICK_COMMANDS_CANVAS)[number]
  | (typeof QUICK_COMMANDS_SKILLS)[number]
  | (typeof QUICK_COMMANDS_SKILLS_DISCUSS)[number]
  | (typeof QUICK_COMMANDS_SCRIPT)[number]
  | (typeof QUICK_COMMANDS_PROJECTS_HOME)[number]
  | (typeof QUICK_COMMANDS_SCENES3D)[number]

function quickCommandLabelKey(cmd: AgentQuickCommand) {
  if ("labelKey" in cmd) return cmd.labelKey
  return cmd.phraseKey
}

function quickCommandsForSurface(surface: AgentQuickSurface): readonly AgentQuickCommand[] {
  if (surface === "shot") return QUICK_COMMANDS_SHOT
  if (surface === "storyboard") return QUICK_COMMANDS_STORYBOARD
  if (surface === "characters") return QUICK_COMMANDS_CHARACTERS
  if (surface === "locations") return QUICK_COMMANDS_LOCATIONS
  if (surface === "props") return QUICK_COMMANDS_PROPS
  if (surface === "canvas") return QUICK_COMMANDS_CANVAS
  if (surface === "skills") return QUICK_COMMANDS_SKILLS
  if (surface === "skills_discuss") return QUICK_COMMANDS_SKILLS_DISCUSS
  if (surface === "script") return QUICK_COMMANDS_SCRIPT
  if (surface === "projects_home") return QUICK_COMMANDS_PROJECTS_HOME
  if (surface === "scenes3d") return QUICK_COMMANDS_SCENES3D
  return []
}

export type AgentShotPromptUpdatedPayload = {
  shotId: number
  target: string
  improvedPrompt: string
  cameraPresetIds?: number[]
  panelCount?: number
}

export type AgentFlowNodePromptUpdatedPayload = {
  nodeId: number
  promptText: string
  promptDoc: Record<string, unknown> | null
}

export type AgentShotDurationUpdatedPayload = {
  shotId: number
  durationTargetSec: number
}

export type AgentShotVideoReferenceRowPayload = {
  shotId: number
  sendAnimatic: boolean
  animaticMode: string
  useShootingBoard: boolean
}

export type AgentShotVideoReferencesUpdatedPayload = {
  shotIds: number[]
  updated: AgentShotVideoReferenceRowPayload[]
}

export type AgentShotCreatedPayload = {
  shotId: number
  number: string
  shotKind: string
}

export type AgentSceneCreatedPayload = {
  sceneId: number
  number: number
  title?: string | null
  episodeId: number
}

export type AgentShotCharactersUpdatedPayload = {
  shotId: number
  characterIds: number[]
  storyboardImagePrompt?: string | null
  videoKlingPrompt?: string | null
  strippedPromptTargets?: string[]
}

export type AgentShotLocationUpdatedPayload = {
  shotId: number
  locationId: number | null
  storyboardImagePrompt?: string | null
  videoKlingPrompt?: string | null
  strippedPromptTargets?: string[]
}

export type AgentShotSceneUpdatedPayload = {
  shotId: number
  sceneId: number
  number: string
  sceneNumber: number
  label: string
  previousSceneId?: number
  previousLabel?: string
}

export type AgentShotsBatchQueuedPayload = {
  generationJobId: number
  shotsCount: number
}

export type AgentEntityUpdatedPayload = {
  entityType: string
  id: number | null
  name: string
  profileText?: string | null
  missingProfileSections?: string[]
  warning?: string | null
  deleted?: boolean
}

export type AgentSelectedEntityPreview = {
  name: string
  imageUrl?: string | null
  /** When no imageUrl, show this instead of name initials. */
  fallbackIcon?: ReactNode
}

export type AgentChatPanelProps = {
  projectId: number
  /**
   * Company canvas assistant uses `/company/agent` + Companies::AgentRunChannel.
   * Defaults to project workspace agent.
   */
  agentScope?: AgentApiScope
  /** Required when agentScope.kind === "company" for ActionCable. */
  companyId?: number
  /** Display name for empty-state copy. */
  projectName?: string
  /** Header title; defaults to the chat's agent name (chatAgentLabelKey in lib/task-agents.ts). */
  title?: string
  mode?: string
  /** Human shot label like "1.2" for header / empty state. */
  shotLabel?: string | null
  /** Shot id→label map for clickable mentions in assistant markdown. */
  shotRefs?: AgentShotRef[]
  contextType: AgentContextType
  contextId: number
  selectedShotId?: number | null
  selectedEntityId?: number | null
  /** Compact thumb+name shown above entity quick-command chips. */
  selectedEntity?: AgentSelectedEntityPreview | null
  /** Mention catalog for resolving @hero[id] → names in saved-prompt cards. */
  mentionItems?: MentionItem[]
  /** Board nodes the composer can mention with `@`. Drives chips in this chat's history. */
  nodeMentionItems?: MentionItem[]
  /** Ids of `@node` tokens currently in the composer. Does not select the node. */
  onMentionedFlowNodeIdsChange?: (ids: number[]) => void
  canvasDraft?: Record<string, unknown> | null
  /** Read at send time so the open 3D editor is not copied into chat state every frame. */
  getScene3dFocus?: () => Scene3dAgentFocus | null
  /** Selected script excerpt + paragraph ids for script_assistant VOLATILE. */
  scriptFocus?: { excerpt: string; block_ids: string[] } | null
  /** Open kanban card. Shots context only; not selectedEntityId (that is a shot id). */
  selectedTaskId?: number | null
  /** Current workspace episode — scopes PROJECT_CONTEXT shots to this series. */
  episodeId?: number | null
  className?: string
  zIndexClass?: string
  /** When true, ignore sidebar/drawer offsets (fullscreen overlays like ShotPanel). */
  overlay?: boolean
  /**
   * Positioning mode:
   * - `viewport` (default): fixed to the bottom of the viewport
   * - `parent`: absolute to the nearest positioned ancestor (e.g. ShotPanel right column)
   * - `inline`: in-flow full width (canvas / entity dock tab content)
   */
  anchor?: "viewport" | "parent" | "inline"
  /**
   * Quick-command chips preset. Defaults from contextType (shot→shot, else project).
   * Shown only when the panel is expanded.
   */
  quickSurface?: AgentQuickSurface
  /** Chip keys from the surface preset to hide (e.g. `createProject` for users without create rights). */
  hiddenQuickCommandKeys?: readonly string[]
  /** Start with the chat history / chips visible (e.g. entity assistant tabs). Ignored when `expanded` is controlled. */
  defaultExpanded?: boolean
  /** Controlled expand state (e.g. canvas panel `!collapsed`). */
  expanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
  /** Header text toggle «Свернуть»/«Развернуть». Default true; canvas hides it and uses chevron. */
  showExpandToggle?: boolean
  /** Collapse when clicking outside the panel. Default true; canvas disables so tab switches do not collapse. */
  collapseOnOutsideClick?: boolean
  /** Stretch card / history to fill parent min-height (canvas assistant tab). */
  fillAvailableHeight?: boolean
  /** Drop the floating card chrome (border / ring / shadow / blur / bg) — for in-column hosts. */
  chromeless?: boolean
  /** Hide the in-panel title row (host renders it in a shared header, e.g. side column). */
  hideTitle?: boolean
  /**
   * Covers the transcript (header, history, chips). The transcript stays mounted.
   * Pair with `bodySlotVisible` so the cover can fade without unmounting.
   */
  bodySlot?: ReactNode
  bodySlotVisible?: boolean
  /**
   * Keep the composer and `composerSlot` stacked in one cell. The taller one sets the
   * footer height; the other stays `invisible` (still in layout). No height animation.
   */
  pinComposerSlot?: boolean
  /** Which stacked layer is interactive when `pinComposerSlot` is set. */
  composerSlotVisible?: boolean
  /** Replaces the composer while the history and quick chips stay visible. */
  composerSlot?: ReactNode
  /** Centered on the top edge of the composer block (e.g. prompt/assistant switcher). */
  composerEdgeOverlay?: ReactNode
  /**
   * When true, overlay spans the composer width (canvas label-collapse measurement).
   * Default false: shrink-wrap + left-1/2 center (entity assistant column).
   */
  composerEdgeOverlayFillWidth?: boolean
  /** Compact shot refs strip under the title (storyboard shot context). */
  contextStrip?: ReactNode
  /**
   * Replaces quick-command chips when set (e.g. storyboard generate buttons).
   * Shot-panel chips stay on `quickSurface` when this is omitted.
   */
  quickActionsSlot?: ReactNode
  /** Right-aligned row above the agent card; moves with expand/collapse. */
  toolbar?: ReactNode
  onOpenShot?: (shotId: number) => void
  onShotPromptUpdated?: (payload: AgentShotPromptUpdatedPayload) => void
  onFlowNodePromptUpdated?: (payload: AgentFlowNodePromptUpdatedPayload) => void
  onFlowBoardChanged?: () => void
  onShotDurationUpdated?: (payload: AgentShotDurationUpdatedPayload) => void
  onShotVideoReferencesUpdated?: (payload: AgentShotVideoReferencesUpdatedPayload) => void
  onShotCreated?: (payload: AgentShotCreatedPayload) => void
  onSceneCreated?: (payload: AgentSceneCreatedPayload) => void
  onShotCharactersUpdated?: (payload: AgentShotCharactersUpdatedPayload) => void
  onShotLocationUpdated?: (payload: AgentShotLocationUpdatedPayload) => void
  onShotSceneUpdated?: (payload: AgentShotSceneUpdatedPayload) => void
  onShotsBatchQueued?: (payload: AgentShotsBatchQueuedPayload) => void
  onEntityUpdated?: (payload: AgentEntityUpdatedPayload) => void
  onCanvasDraftUpdated?: (patch: AgentCanvasDraftUpdated) => void
  onScene3dLayoutUpdated?: (payload: Scene3dLayoutUpdated) => void
  onCanvasGenerationRequested?: () => void
  onAgentActionConfirmed?: (result: AgentConfirmActionResult, action: AgentPendingAction) => void
  /** Fires when run starts/stops thinking/streaming (for hosts that gate scroll-collapse). */
  onBusyChange?: (busy: boolean) => void
  /** Успешный конец прогона: короткий текст последнего ответа (может быть пустым). */
  onAssistantSettled?: (preview: string) => void
  /** When set, send once after history is ready (e.g. extract-via-agent from toolbar). */
  autoSendMessage?: string | null
  /** After successful POST or exhausted retries — releases pending auto-send. */
  onAutoSendSettled?: () => void
  /** Host handles extract chip (clear selection + queue auto-send). */
  onQuickExtractCharacters?: () => void
  onQuickExtractLocations?: () => void
  /**
   * Host should show the agent composer (e.g. switch to the Assistant tab).
   * Fired for quick chips and when the user starts editing a message.
   */
  onQuickCommandUsed?: () => void
  /**
   * When false (project viewer / support), history still loads but send / confirm / dictation are off.
   * Defaults from `project_permissions.can_edit_content` for project scope; company/studio stay editable.
   */
  canEditContent?: boolean
}

type AgentCablePayload =
  | { type: "run_delta"; phase?: string | null; buffer?: string; tool_name?: string | null }
  | { type: "run"; run: AgentRunState; last_message_id?: number | null }
  | { type: "handoff"; handoff: { message_id: number; status: "running" | "done" | "failed" } }

function notifyFlowNodePromptUpdated(
  payload: AgentFlowNodePromptUpdated | null | undefined,
  onFlowNodePromptUpdated?: (payload: AgentFlowNodePromptUpdatedPayload) => void,
) {
  if (!payload || !onFlowNodePromptUpdated) return
  const nodeId = Number(payload.node_id)
  const promptText = payload.prompt_text
  if (!Number.isFinite(nodeId) || nodeId <= 0 || !promptText) return
  const promptDoc =
    payload.prompt_doc && typeof payload.prompt_doc === "object" && !Array.isArray(payload.prompt_doc)
      ? payload.prompt_doc
      : null
  onFlowNodePromptUpdated({ nodeId, promptText, promptDoc })
}

function notifyShotPromptUpdated(
  payload: AgentShotPromptUpdated | AgentShotPromptUpdated[] | null | undefined,
  onShotPromptUpdated?: (payload: AgentShotPromptUpdatedPayload) => void,
) {
  if (!payload || !onShotPromptUpdated) return
  const items = Array.isArray(payload) ? payload : [payload]
  for (const item of items) {
    const shotId = Number(item?.shot_id)
    const improvedPrompt = item?.improved_prompt
    if (!Number.isFinite(shotId) || shotId <= 0 || !improvedPrompt) continue
    const cameraPresetIds = Array.isArray(item.camera_preset_ids)
      ? item.camera_preset_ids.map(Number).filter((id) => Number.isFinite(id) && id > 0)
      : undefined
    const panelCount = Number(item.panel_count)
    onShotPromptUpdated({
      shotId,
      target: item.target,
      improvedPrompt,
      ...(cameraPresetIds && cameraPresetIds.length > 0 ? { cameraPresetIds } : {}),
      ...(Number.isFinite(panelCount) && panelCount > 0 ? { panelCount } : {}),
    })
  }
}

function mergeCanvasDraftForConfirm(
  draft: Record<string, unknown> | null | undefined,
  patch: AgentCanvasDraftUpdated | null | undefined,
): Record<string, unknown> | null | undefined {
  if (!patch) return draft
  const next: Record<string, unknown> = { ...(draft ?? {}) }
  if (patch.media_kind) next.media_kind = patch.media_kind
  if (patch.model_id) next.model_id = patch.model_id
  if (patch.aspect_ratio) next.aspect_ratio = patch.aspect_ratio
  if (patch.duration_sec != null) next.duration_sec = patch.duration_sec
  if (patch.prompt_text_by_kind) {
    const prev =
      next.prompt_text_by_kind && typeof next.prompt_text_by_kind === "object"
        ? (next.prompt_text_by_kind as Record<string, unknown>)
        : {}
    next.prompt_text_by_kind = { ...prev, ...patch.prompt_text_by_kind }
  }
  const kind = next.media_kind === "image" || next.media_kind === "video" ? next.media_kind : undefined
  const byKind = next.prompt_text_by_kind as { image?: string; video?: string } | undefined
  if (kind && typeof byKind?.[kind] === "string") {
    next.prompt_text = byKind[kind]
  } else if (patch.prompt_text != null) {
    next.prompt_text = patch.prompt_text
  }
  return next
}

function notifyShotDurationUpdated(
  payload: AgentShotDurationUpdated | null | undefined,
  onShotDurationUpdated?: (payload: AgentShotDurationUpdatedPayload) => void,
) {
  if (!payload || !onShotDurationUpdated) return
  onShotDurationUpdated({
    shotId: payload.shot_id,
    durationTargetSec: payload.duration_target_sec,
  })
}

function notifyShotVideoReferencesUpdated(
  payload: AgentShotVideoReferencesUpdated | null | undefined,
  onShotVideoReferencesUpdated?: (payload: AgentShotVideoReferencesUpdatedPayload) => void,
) {
  if (!payload || !onShotVideoReferencesUpdated) return
  const shotIds = Array.isArray(payload.shot_ids) ? payload.shot_ids.filter((id) => Number.isFinite(id) && id > 0) : []
  const updated = (payload.updated ?? []).map((row) => ({
    shotId: row.shot_id,
    sendAnimatic: Boolean(row.send_animatic),
    animaticMode: row.animatic_mode,
    useShootingBoard: Boolean(row.send_shooting_board),
  }))
  onShotVideoReferencesUpdated({ shotIds, updated })
}

function notifyShotCreated(
  payload: AgentShotCreated | null | undefined,
  onShotCreated?: (payload: AgentShotCreatedPayload) => void,
) {
  if (!payload || !onShotCreated) return
  onShotCreated({
    shotId: payload.shot_id,
    number: payload.number,
    shotKind: payload.shot_kind,
  })
}

function notifySceneCreated(
  payload: AgentSceneCreated | null | undefined,
  onSceneCreated?: (payload: AgentSceneCreatedPayload) => void,
) {
  if (!payload || !onSceneCreated) return
  onSceneCreated({
    sceneId: payload.scene_id,
    number: payload.number,
    title: payload.title,
    episodeId: payload.episode_id,
  })
}

function notifyShotCharactersUpdated(
  payload: AgentShotCharactersUpdated | null | undefined,
  onShotCharactersUpdated?: (payload: AgentShotCharactersUpdatedPayload) => void,
) {
  if (!payload || !onShotCharactersUpdated) return
  onShotCharactersUpdated({
    shotId: payload.shot_id,
    characterIds: Array.isArray(payload.character_ids) ? payload.character_ids : [],
    storyboardImagePrompt: payload.storyboard_image_prompt,
    videoKlingPrompt: payload.video_kling_prompt,
    strippedPromptTargets: Array.isArray(payload.stripped_prompt_targets) ? payload.stripped_prompt_targets : [],
  })
}

function notifyShotLocationUpdated(
  payload: AgentShotLocationUpdated | null | undefined,
  onShotLocationUpdated?: (payload: AgentShotLocationUpdatedPayload) => void,
) {
  if (!payload || !onShotLocationUpdated) return
  onShotLocationUpdated({
    shotId: payload.shot_id,
    locationId: payload.location_id ?? null,
    storyboardImagePrompt: payload.storyboard_image_prompt,
    videoKlingPrompt: payload.video_kling_prompt,
    strippedPromptTargets: Array.isArray(payload.stripped_prompt_targets) ? payload.stripped_prompt_targets : [],
  })
}

function notifyShotSceneUpdated(
  payload: AgentShotSceneUpdated | null | undefined,
  onShotSceneUpdated?: (payload: AgentShotSceneUpdatedPayload) => void,
) {
  if (!payload || !onShotSceneUpdated) return
  onShotSceneUpdated({
    shotId: payload.shot_id,
    sceneId: payload.scene_id,
    number: String(payload.number),
    sceneNumber: payload.scene_number,
    label: payload.label,
    previousSceneId: payload.previous_scene_id,
    previousLabel: payload.previous_label,
  })
}

function notifyShotsBatchQueued(
  payload: AgentShotsBatchQueued | null | undefined,
  onShotsBatchQueued?: (payload: AgentShotsBatchQueuedPayload) => void,
) {
  if (!payload || !onShotsBatchQueued) return
  onShotsBatchQueued({
    generationJobId: payload.generation_job_id,
    shotsCount: payload.shots_count,
  })
}

function metaFingerprint(meta: Record<string, unknown> | null | undefined): string {
  if (!meta || typeof meta !== "object") return ""
  try {
    return JSON.stringify(meta)
  } catch {
    return ""
  }
}

function messagesChanged(prev: AgentMessageRow[], next: AgentMessageRow[]): boolean {
  if (prev.length !== next.length) return true
  if (prev.length === 0) return false
  const a = prev[prev.length - 1]
  const b = next[next.length - 1]
  return (
    a.id !== b.id || a.content !== b.content || a.role !== b.role || metaFingerprint(a.meta) !== metaFingerprint(b.meta)
  )
}

function optimisticAgentImages(images: readonly PendingAgentImage[]): AgentMessageImage[] {
  return images.map((image, index) => {
    const uploadedVideo = image.kind === "upload" && image.mediaType === "video"
    const contentType =
      image.kind === "upload" && image.file.type
        ? image.file.type
        : image.mediaType === "video"
          ? "video/mp4"
          : "image/jpeg"
    const libraryVideo = image.kind === "library" && image.mediaType === "video"
    return {
      id: -index - 1,
      filename: image.kind === "upload" ? image.file.name : image.label,
      content_type: contentType,
      preview_url: image.previewUrl,
      thumb_url: uploadedVideo ? null : image.previewUrl,
      stream_url: uploadedVideo ? image.previewUrl : null,
      // Постер библиотечного ролика — картинка. В download_url плеер принял бы её за файл.
      download_url: libraryVideo ? null : image.previewUrl,
    }
  })
}

function messageListKey(m: AgentMessageRow): string {
  return m.clientKey ?? `msg-${m.id}`
}

function historyHasReplacingAssistant(messages: AgentMessageRow[], afterId: number): boolean {
  return messages.some((m) => {
    if (m.role !== "assistant" || m.id <= afterId) return false
    if (m.content && m.content.trim().length > 0) return true
    return parseSavedPromptCards(m.meta).length > 0
  })
}

function findLastOwnUserMessage(
  messages: AgentMessageRow[],
  currentUserEmail: string | null,
): AgentMessageRow | undefined {
  return [...messages]
    .reverse()
    .find((m) => m.role === "user" && m.id > 0 && (!m.author?.email || m.author.email === currentUserEmail))
}

function findLastRetryableUserMessage(
  messages: AgentMessageRow[],
  currentUserEmail: string | null,
  isSuperAdmin: boolean,
): AgentMessageRow | undefined {
  if (isSuperAdmin) {
    return [...messages].reverse().find((m) => m.role === "user" && m.id > 0)
  }
  return findLastOwnUserMessage(messages, currentUserEmail)
}

const ASSISTANT_REPLY_PREVIEW_MAX = 140

function assistantReplyPreview(buffer: string, messages: AgentMessageRow[], baselineId: number): string {
  const fresh = [...messages]
    .reverse()
    .find((message) => message.role === "assistant" && message.id > baselineId && message.content?.trim())
  const raw = (fresh?.content || buffer).replace(/\s+/g, " ").trim()
  if (raw.length <= ASSISTANT_REPLY_PREVIEW_MAX) return raw
  return `${raw.slice(0, ASSISTANT_REPLY_PREVIEW_MAX - 1).trimEnd()}…`
}

function notifyEntityUpdated(
  payload: AgentEntityUpdated | null | undefined,
  onEntityUpdated?: (payload: AgentEntityUpdatedPayload) => void,
) {
  if (!payload || !onEntityUpdated) return
  onEntityUpdated({
    entityType: payload.entity_type,
    id: payload.id,
    name: payload.name,
    profileText: payload.profile_text,
    missingProfileSections: payload.missing_profile_sections,
    warning: payload.warning,
    deleted: payload.deleted === true,
  })
}

export function AgentChatPanel({
  projectId,
  agentScope: agentScopeProp,
  companyId,
  projectName,
  mode = "shots_assistant",
  shotLabel = null,
  shotRefs = [],
  contextType,
  contextId,
  selectedShotId = null,
  selectedEntityId = null,
  selectedEntity = null,
  mentionItems,
  nodeMentionItems,
  onMentionedFlowNodeIdsChange,
  canvasDraft = null,
  getScene3dFocus,
  scriptFocus = null,
  selectedTaskId = null,
  episodeId = null,
  className,
  zIndexClass = "z-40",
  overlay = false,
  anchor = "viewport",
  quickSurface,
  hiddenQuickCommandKeys,
  defaultExpanded = false,
  expanded: expandedProp,
  onExpandedChange,
  showExpandToggle = true,
  collapseOnOutsideClick = true,
  fillAvailableHeight = false,
  chromeless = false,
  hideTitle = false,
  title,
  bodySlot,
  bodySlotVisible = false,
  pinComposerSlot = false,
  composerSlotVisible = false,
  composerSlot,
  composerEdgeOverlay,
  composerEdgeOverlayFillWidth = false,
  contextStrip,
  quickActionsSlot,
  toolbar,
  onOpenShot,
  onShotPromptUpdated,
  onFlowNodePromptUpdated,
  onFlowBoardChanged,
  onShotDurationUpdated,
  onShotVideoReferencesUpdated,
  onShotCreated,
  onSceneCreated,
  onShotCharactersUpdated,
  onShotLocationUpdated,
  onShotSceneUpdated,
  onShotsBatchQueued,
  onEntityUpdated,
  onCanvasDraftUpdated,
  onScene3dLayoutUpdated,
  onCanvasGenerationRequested,
  onAgentActionConfirmed,
  onBusyChange,
  onAssistantSettled,
  autoSendMessage = null,
  onAutoSendSettled,
  onQuickExtractCharacters,
  onQuickExtractLocations,
  onQuickCommandUsed,
  canEditContent: canEditContentProp,
}: AgentChatPanelProps) {
  const { t, locale } = useTranslation("workspace")
  // Resolve by value — parent often passes a fresh `{ kind: "company" }` each render.
  const agentScopeKind = agentScopeProp?.kind
  const agentScopeProjectId = agentScopeProp?.kind === "project" ? agentScopeProp.projectId : undefined
  const agentScopeCompanyId = agentScopeProp?.kind === "company" ? agentScopeProp.companyId : undefined
  const resolvedAgentScope = useMemo<AgentApiScope>(() => {
    if (agentScopeKind === "company") {
      return agentScopeCompanyId != null && agentScopeCompanyId > 0
        ? { kind: "company", companyId: agentScopeCompanyId }
        : { kind: "company" }
    }
    if (agentScopeKind === "studio") return { kind: "studio" }
    if (agentScopeKind === "project" && agentScopeProjectId != null) {
      return { kind: "project", projectId: agentScopeProjectId }
    }
    return { kind: "project", projectId }
  }, [agentScopeKind, agentScopeProjectId, agentScopeCompanyId, projectId])
  const pageProject = usePage().props as { project?: { frame_format?: string } }
  const frameFormat = pageProject.project?.frame_format || "16:9"
  const libraryProjectId = resolvedAgentScope.kind === "project" && projectId > 0 ? projectId : null
  const pagePermissions = usePage<{ project_permissions?: { can_edit_content?: boolean } }>().props.project_permissions
  const canEditContent =
    canEditContentProp ?? (resolvedAgentScope.kind === "project" ? pagePermissions?.can_edit_content !== false : true)
  const currentUser = usePage<{
    auth?: {
      user?: {
        email?: string
        first_name?: string | null
        last_name?: string | null
        super_admin?: boolean
      } | null
    }
  }>().props.auth?.user
  const currentUserEmail = currentUser?.email ?? null
  const isSuperAdmin = currentUser?.super_admin === true
  const sidebarCollapsed = useSidebarCollapsed(true)
  const drawerContentRight = useShellDrawerContentRight()
  const anchorParent = anchor === "parent"
  const anchorInline = anchor === "inline"
  const resolvedQuickSurface: AgentQuickSurface = quickSurface ?? (contextType === "shot" ? "shot" : "project")
  const projectsHomeSurface = resolvedQuickSurface === "projects_home"
  const hiddenQuickKeys = hiddenQuickCommandKeys?.join("|") ?? ""
  const quickCommands = useMemo(() => {
    const all = quickCommandsForSurface(resolvedQuickSurface)
    if (!hiddenQuickKeys) return all
    const hidden = new Set(hiddenQuickKeys.split("|"))
    return all.filter((cmd) => !hidden.has(cmd.key))
  }, [resolvedQuickSurface, hiddenQuickKeys])

  const [messages, setMessages] = useState<AgentMessageRow[]>([])
  const messagesRef = useRef(messages)
  messagesRef.current = messages
  const onAssistantSettledRef = useRef(onAssistantSettled)
  onAssistantSettledRef.current = onAssistantSettled
  const replyBaselineIdRef = useRef(0)
  const [pendingAction, setPendingAction] = useState<AgentPendingAction | null>(null)
  const { input, setInput } = useAgentComposerDraft({
    projectId,
    mode,
    contextType,
    contextId,
  })
  const {
    pendingImages,
    setPendingImages,
    pendingImagesRef,
    epochRef,
    ready: imagesReady,
    whenReady: whenImagesReady,
  } = useAgentComposerImages(
    { projectId, mode, contextType, contextId },
    { onQuotaExceeded: () => toast.error(t("agent.imageDraftQuota")) },
  )
  const onMentionedFlowNodeIdsChangeRef = useRef(onMentionedFlowNodeIdsChange)
  onMentionedFlowNodeIdsChangeRef.current = onMentionedFlowNodeIdsChange

  useEffect(() => {
    onMentionedFlowNodeIdsChangeRef.current?.(flowNodeMentionIdsFromText(input))
  }, [input, contextId, contextType])

  useEffect(() => {
    return () => onMentionedFlowNodeIdsChangeRef.current?.([])
  }, [])
  const [availableSkills, setAvailableSkills] = useState<AgentSessionSkill[]>([])
  const [streaming, setStreaming] = useState(false)
  const [streamBuffer, setStreamBuffer] = useState("")
  const [agentPhase, setAgentPhase] = useState<"thinking" | "tool" | null>(null)
  const [agentToolName, setAgentToolName] = useState<string | null>(null)
  const [requestStartedAt, setRequestStartedAt] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [networkAutoRetryArmed, setNetworkAutoRetryArmed] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [stopping, setStopping] = useState(false)
  const [queuedMessage, setQueuedMessage] = useState<string | null>(null)
  const queuedImagesRef = useRef<PendingAgentImage[]>([])
  const lastAttemptedImagesRef = useRef<PendingAgentImage[]>([])
  const [queueConfirm, setQueueConfirm] = useState<{ text: string; replace: boolean } | null>(null)
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null)
  const imageMentionItems = useMemo(() => {
    if (editingMessageId != null) {
      const editing = messages.find((message) => message.id === editingMessageId)
      return agentImageMentionItems(
        (editing?.images ?? []).map((image) => ({
          mediaType: image.content_type?.startsWith("video/") ? ("video" as const) : ("image" as const),
          previewUrl: image.content_type?.startsWith("video/") ? null : image.thumb_url || image.download_url || null,
        })),
      )
    }
    return agentImageMentionItems(
      pendingImages.map((image) => ({
        mediaType: image.mediaType,
        previewUrl: image.mediaType === "video" ? null : image.previewUrl,
      })),
    )
  }, [editingMessageId, messages, pendingImages])
  const [editConfirmOpen, setEditConfirmOpen] = useState(false)
  const [expandedState, setExpandedState] = useState(defaultExpanded)
  const expandedControlled = expandedProp !== undefined
  const expanded = expandedControlled ? expandedProp : expandedState
  const setExpanded = useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      const value = typeof next === "function" ? next(expanded) : next
      if (!expandedControlled) setExpandedState(value)
      onExpandedChange?.(value)
    },
    [expanded, expandedControlled, onExpandedChange],
  )

  useEffect(() => {
    onBusyChange?.(streaming)
    return () => {
      if (streaming) onBusyChange?.(false)
    }
  }, [streaming, onBusyChange])

  const [canContinue, setCanContinue] = useState(false)
  const [historyReady, setHistoryReady] = useState(false)
  const [historyPaneVisible, setHistoryPaneVisible] = useState(true)
  const [hasOlder, setHasOlder] = useState(false)
  const reducedMotion = useReducedMotion()
  const reducedMotionRef = useRef(reducedMotion)
  reducedMotionRef.current = reducedMotion
  const firstHistoryKeyRef = useRef(true)
  const historyReadyRef = useRef(false)
  const fadeOutDoneRef = useRef(true)
  const holdHistoryUntilFadeRef = useRef(false)
  const pendingHistoryRef = useRef<AgentHistoryResponse | null>(null)
  const historyScrollRef = useRef<HTMLDivElement | null>(null)
  const hasOlderRef = useRef(false)
  const loadingOlderRef = useRef(false)
  const olderAbortRef = useRef<AbortController | null>(null)
  const stickToBottomRef = useRef(true)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const composerRef = useRef<AgentComposerEditorHandle | null>(null)
  const quickRowRef = useRef<HTMLDivElement | null>(null)
  const measureRowRef = useRef<HTMLDivElement | null>(null)
  const entityBadgeRef = useRef<HTMLDivElement | null>(null)
  const promptNotifyRef = useRef(false)
  const flowNodePromptNotifyRef = useRef(false)
  const flowBoardNotifyRef = useRef(false)
  const durationNotifyRef = useRef(false)
  const videoRefsNotifyRef = useRef(false)
  const shotCreatedNotifyRef = useRef(false)
  const charactersNotifyRef = useRef(false)
  const locationNotifyRef = useRef(false)
  const sceneUpdatedNotifyRef = useRef(false)
  const batchQueuedNotifyRef = useRef(false)
  const entityNotifyRef = useRef(false)
  const canvasDraftNotifyRef = useRef(false)
  const scene3dLayoutNotifyRef = useRef<string | null>(null)
  const canvasGenerationNotifyRef = useRef(false)
  const sceneCreatedNotifyRef = useRef(false)
  const notesNotifyRef = useRef(false)
  const resetRunEventLatches = useCallback(() => {
    promptNotifyRef.current = false
    flowNodePromptNotifyRef.current = false
    flowBoardNotifyRef.current = false
    durationNotifyRef.current = false
    videoRefsNotifyRef.current = false
    shotCreatedNotifyRef.current = false
    charactersNotifyRef.current = false
    locationNotifyRef.current = false
    sceneUpdatedNotifyRef.current = false
    batchQueuedNotifyRef.current = false
    entityNotifyRef.current = false
    canvasDraftNotifyRef.current = false
    scene3dLayoutNotifyRef.current = null
    canvasGenerationNotifyRef.current = false
    sceneCreatedNotifyRef.current = false
    notesNotifyRef.current = false
  }, [])
  const lastEventsRef = useRef<AgentRunLastEvents | null>(null)
  const streamBufferRef = useRef("")
  const wasProcessingRef = useRef(false)
  const runWatchRef = useRef<RunWatch | null>(null)
  const lastMessageIdRef = useRef<number | null>(null)
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pollAbortRef = useRef<AbortController | null>(null)
  const pollBackoffMsRef = useRef(RUN_STATE_FALLBACK_MS)
  const [cableConnected, setCableConnected] = useState(false)
  const loadHistoryRef = useRef<(() => Promise<void>) | null>(null)
  const applyHistoryRef = useRef<((data: AgentHistoryResponse) => "processing" | "failed" | "idle") | null>(null)
  const notifyErrorRef = useRef<(message: string, opts?: { code?: string | null; toast?: boolean }) => void>(
    () => undefined,
  )
  const historyLoadErrorToastedKeyRef = useRef<string | null>(null)
  const historyAbortRef = useRef<AbortController | null>(null)
  const autoSendConsumedRef = useRef(false)
  const autoSendAttemptsRef = useRef(0)
  const autoSendAliveRef = useRef(true)
  const queuedMessageRef = useRef<string | null>(null)
  const sendingRef = useRef(false)
  const sendGenRef = useRef(0)
  const lastAttemptedMessageRef = useRef<string | null>(null)
  const networkRetryLoopRef = useRef(false)
  const allowQueueDrainRef = useRef(false)
  const [lastAttemptedMessage, setLastAttemptedMessage] = useState<string | null>(null)
  const [queueKick, setQueueKick] = useState(0)
  const [autoSendRetryTick, setAutoSendRetryTick] = useState(0)
  const [visibleQuickCount, setVisibleQuickCount] = useState(0)
  const hasQuickActionsSlot = Boolean(quickActionsSlot) && canEditContent
  const hasQuickCommands = !hasQuickActionsSlot && canEditContent && quickCommands.length > 0
  const showQuickCommands = expanded && hasQuickCommands
  const showQuickRow = expanded && (hasQuickActionsSlot || hasQuickCommands || Boolean(selectedEntity))

  /**
   * Сбой того, что пользователь только что запустил сам: текст уходит во флеш-уведомление,
   * а в состоянии остаётся для кнопок «Продолжить»/«Повторить».
   *
   * Уведомляем только о событиях. Ошибка, поднятая из состояния прогона при открытии
   * панели, сюда не идёт: она не новость для пользователя, а тост на каждое обновление
   * страницы — шум (см. applyRunState).
   */
  const notifyError = useCallback((message: string, opts?: { code?: string | null; toast?: boolean }) => {
    setError(message)
    setErrorCode(opts?.code ?? null)
    if (opts?.toast !== false) toast.error(message)
  }, [])
  notifyErrorRef.current = notifyError

  const errorMessageForRun = useCallback(
    (run: AgentRunState | null | undefined) => {
      const code = run?.error_code
      if (code === "interrupted") return t("agent.interrupted")
      if (code === "internal") return t("agent.internalError")
      if (code === "network") return t("agent.networkError")
      if (code === "busy") return t("agent.busy")
      if (code === "rate_limited") return t("agent.rateLimited")
      if (code === "vision") return t("agent.imageFailed")
      return run?.error || t("agent.error")
    },
    [t],
  )

  const dispatchRunLastEvents = useCallback(
    (run: AgentRunState | null | undefined) => {
      const events = run?.last_events
      if (!events) return
      if (!promptNotifyRef.current && events.shot_prompt_updated) {
        promptNotifyRef.current = true
        notifyShotPromptUpdated(events.shot_prompt_updated, onShotPromptUpdated)
      }
      if (!flowNodePromptNotifyRef.current && events.flow_node_prompt_updated) {
        flowNodePromptNotifyRef.current = true
        notifyFlowNodePromptUpdated(events.flow_node_prompt_updated, onFlowNodePromptUpdated)
      }
      if (!flowBoardNotifyRef.current && events.flow_changed) {
        flowBoardNotifyRef.current = true
        onFlowBoardChanged?.()
      }
      if (!durationNotifyRef.current && events.shot_duration_updated) {
        durationNotifyRef.current = true
        notifyShotDurationUpdated(events.shot_duration_updated, onShotDurationUpdated)
      }
      if (!videoRefsNotifyRef.current && events.shot_video_references_updated) {
        videoRefsNotifyRef.current = true
        notifyShotVideoReferencesUpdated(events.shot_video_references_updated, onShotVideoReferencesUpdated)
      }
      if (!shotCreatedNotifyRef.current && events.shot_created) {
        shotCreatedNotifyRef.current = true
        notifyShotCreated(events.shot_created, onShotCreated)
      }
      if (!charactersNotifyRef.current && events.shot_characters_updated) {
        charactersNotifyRef.current = true
        notifyShotCharactersUpdated(events.shot_characters_updated, onShotCharactersUpdated)
      }
      if (!locationNotifyRef.current && events.shot_location_updated) {
        locationNotifyRef.current = true
        notifyShotLocationUpdated(events.shot_location_updated, onShotLocationUpdated)
      }
      if (!sceneUpdatedNotifyRef.current && events.shot_scene_updated) {
        sceneUpdatedNotifyRef.current = true
        notifyShotSceneUpdated(events.shot_scene_updated, onShotSceneUpdated)
      }
      if (!batchQueuedNotifyRef.current && events.shots_batch_queued) {
        batchQueuedNotifyRef.current = true
        notifyShotsBatchQueued(events.shots_batch_queued, onShotsBatchQueued)
      }
      if (!entityNotifyRef.current && events.entity_updated) {
        entityNotifyRef.current = true
        notifyEntityUpdated(events.entity_updated, onEntityUpdated)
        const updated = events.entity_updated
        if (updated.entity_type === "scene3d" && updated.deleted !== true && updated.id != null && projectId) {
          void ensureScene3dPosterIfMissing(projectId, updated.id)
        }
      }
      if (!canvasDraftNotifyRef.current && events.canvas_draft_updated) {
        canvasDraftNotifyRef.current = true
        onCanvasDraftUpdated?.(events.canvas_draft_updated)
      }
      if (events.scene3d_layout_updated) {
        const fingerprint = JSON.stringify(events.scene3d_layout_updated)
        if (fingerprint !== scene3dLayoutNotifyRef.current) {
          scene3dLayoutNotifyRef.current = fingerprint
          onScene3dLayoutUpdated?.(events.scene3d_layout_updated)
        }
      }
      if (!canvasGenerationNotifyRef.current && events.canvas_generation_requested?.start_generation) {
        canvasGenerationNotifyRef.current = true
        onCanvasGenerationRequested?.()
      }
      if (!notesNotifyRef.current && events.notes_updated) {
        notesNotifyRef.current = true
        notifyNotesChanged()
      }
      if (!sceneCreatedNotifyRef.current && events.scene_created) {
        sceneCreatedNotifyRef.current = true
        notifySceneCreated(events.scene_created, onSceneCreated)
      }
    },
    [
      onShotPromptUpdated,
      onFlowNodePromptUpdated,
      onFlowBoardChanged,
      onShotDurationUpdated,
      onShotVideoReferencesUpdated,
      onShotCreated,
      onSceneCreated,
      onShotCharactersUpdated,
      onShotLocationUpdated,
      onShotSceneUpdated,
      onShotsBatchQueued,
      onEntityUpdated,
      projectId,
      onCanvasDraftUpdated,
      onScene3dLayoutUpdated,
      onCanvasGenerationRequested,
    ],
  )

  const scopeCompanyId = resolvedAgentScope.kind === "company" ? (resolvedAgentScope.companyId ?? "") : ""
  const historyContextKey = `${resolvedAgentScope.kind}:${scopeCompanyId}:${projectId}:${mode}:${contextType}:${contextId}`
  const historyContextKeyRef = useRef(historyContextKey)
  historyContextKeyRef.current = historyContextKey

  const applyRunState = useCallback(
    (
      run: AgentRunState | null | undefined,
      opts?: { lastMessageId?: number | null; reloadHistory?: boolean },
    ): "processing" | "failed" | "idle" => {
      const status = run?.status ?? "idle"
      // Снимок прошлого прогона приходит вместе с отправкой и сжигает last_events.
      if (classifyRunSnapshot(runWatchRef.current, run) === "ignore") return "processing"
      const active = isRunActive(status)
      if (active && runWatchRef.current) {
        runWatchRef.current = watchAfterActiveRun(runWatchRef.current, run?.started_at)
      }
      if (run?.last_events) lastEventsRef.current = run.last_events
      const remoteLastId = opts?.lastMessageId
      const previousLastId = lastMessageIdRef.current ?? 0
      let gotNewMessage = false
      if (typeof remoteLastId === "number") {
        if (remoteLastId > previousLastId) {
          lastMessageIdRef.current = remoteLastId
          gotNewMessage = true
          if (opts?.reloadHistory !== false) {
            void loadHistoryRef.current?.()
          }
        }
      }

      if (active) {
        if (!wasProcessingRef.current) replyBaselineIdRef.current = previousLastId
        wasProcessingRef.current = true
        allowQueueDrainRef.current = false
        setStreaming(true)
        setStopping(status === "cancel_requested")
        setCanContinue(false)
        setError(null)
        setErrorCode(null)
        setNetworkAutoRetryArmed(false)
        const phase = run?.phase === "tool" ? "tool" : "thinking"
        setAgentPhase(phase)
        setAgentToolName(typeof run?.tool_name === "string" ? run.tool_name : null)
        setRequestStartedAt(run?.started_at ?? null)
        // Do not clear streamBuffer on last_message_id bump — history may lag and
        // wiping the live bubble causes assistant flicker. Cleared in applyHistory
        // once a replacing assistant row is present, or on idle below.
        if (typeof run?.stream_buffer === "string") {
          const buf = run.stream_buffer
          // Avoid rewinding live cable deltas with a lagged DB snapshot.
          if (buf.length >= streamBufferRef.current.length) {
            streamBufferRef.current = buf
            setStreamBuffer(buf)
          }
        }
        return "processing"
      }

      // Уведомляем только о прогоне, который панель видела активным (отправка или живой processing).
      // Снимок прошлого прогона сюда не доходит: его отсекает classifyRunSnapshot.
      const wasProcessing = wasProcessingRef.current
      if (wasProcessing) {
        dispatchRunLastEvents(run)
        // Final displayable row already triggered loadHistory via last_message_id.
        // Skip a duplicate fetch on idle unless this transition brought a new id.
        if (gotNewMessage) {
          void loadHistoryRef.current?.()
        }
        if (status !== "failed") {
          const preview = assistantReplyPreview(
            streamBufferRef.current,
            messagesRef.current,
            replyBaselineIdRef.current,
          )
          if (preview || gotNewMessage) onAssistantSettledRef.current?.(preview)
        }
      }
      wasProcessingRef.current = false
      runWatchRef.current = null
      setStreaming(false)
      setStopping(false)
      setAgentPhase(null)
      setAgentToolName(null)
      setRequestStartedAt(null)
      streamBufferRef.current = ""
      setStreamBuffer("")

      if (status === "failed") {
        allowQueueDrainRef.current = false
        const message = errorMessageForRun(run)
        const code = run?.error_code ?? null
        const liveNetwork = wasProcessing && code === "network"
        setNetworkAutoRetryArmed(liveNetwork)
        // Провал, случившийся при нас, — событие: о нём уведомляем. Прогон, уже
        // провалившийся к моменту открытия панели, — состояние: оно поднимает кнопки
        // «Продолжить»/«Повторить», но тост на каждое обновление страницы не шлёт.
        // Витки автоповтора network не тостим повторно.
        if (wasProcessing) {
          notifyError(message, {
            code,
            toast: !(code === "network" && networkRetryLoopRef.current),
          })
        } else {
          setError(message)
          setErrorCode(code)
        }
        // «Продолжить» шлёт новую реплику и не сбрасывает кэш разбора — картинку так не повторить.
        setCanContinue(code !== "vision")
        return "failed"
      }

      allowQueueDrainRef.current = true
      return "idle"
    },
    [dispatchRunLastEvents, errorMessageForRun, notifyError],
  )

  const applyHistory = useCallback(
    (data: AgentHistoryResponse) => {
      const nextMessages = data.messages.filter((m) => m.role === "user" || m.role === "assistant")
      const pageHasOlder = data.has_older === true
      const oldestIncoming = oldestPositiveId(nextMessages)
      const keptPrefix =
        pageHasOlder &&
        oldestIncoming != null &&
        messagesRef.current.some((row) => row.id > 0 && row.id < oldestIncoming)
      if (!keptPrefix) {
        hasOlderRef.current = pageHasOlder
        setHasOlder(pageHasOlder)
      }
      // Синхронно, до applyRunState: превью ответа читает ref, а не ещё не закоммиченный state.
      messagesRef.current = mergeHistoryMessages(messagesRef.current, nextMessages, pageHasOlder)
      setMessages((prev) => {
        const merged = mergeHistoryMessages(prev, nextMessages, pageHasOlder)
        messagesRef.current = merged
        return messagesChanged(prev, merged) ? merged : prev
      })
      setPendingAction(data.pending_action)
      if (data.available_skills) setAvailableSkills(data.available_skills)
      const appliedLastId =
        typeof data.last_message_id === "number"
          ? data.last_message_id
          : nextMessages.length > 0
            ? Math.max(...nextMessages.map((m) => m.id))
            : lastMessageIdRef.current
      if (typeof appliedLastId === "number") {
        const prevId = lastMessageIdRef.current ?? 0
        lastMessageIdRef.current = appliedLastId
        // Clear live stream only once history actually contains a replacing assistant.
        if (appliedLastId > prevId && historyHasReplacingAssistant(nextMessages, prevId)) {
          streamBufferRef.current = ""
          setStreamBuffer("")
        }
      }
      return applyRunState(data.run, {
        lastMessageId: data.last_message_id,
        reloadHistory: false,
      })
    },
    [applyRunState],
  )
  applyHistoryRef.current = applyHistory

  const loadHistory = useCallback(async () => {
    historyAbortRef.current?.abort()
    const ac = new AbortController()
    historyAbortRef.current = ac
    const requestKey = historyContextKey
    try {
      const data = await fetchAgentHistory({
        projectId,
        agentScope: resolvedAgentScope,
        mode,
        contextType,
        contextId,
        signal: ac.signal,
      })
      // Abort does not cover a resolution between commit and effect cleanup.
      if (ac.signal.aborted || historyContextKeyRef.current !== requestKey) return
      historyLoadErrorToastedKeyRef.current = null
      if (holdHistoryUntilFadeRef.current && !fadeOutDoneRef.current) {
        pendingHistoryRef.current = data
        return
      }
      applyHistoryRef.current?.(data)
      historyReadyRef.current = true
      setHistoryReady(true)
      if (fadeOutDoneRef.current) setHistoryPaneVisible(true)
    } catch (e) {
      if (ac.signal.aborted || historyContextKeyRef.current !== requestKey) return
      if (e instanceof DOMException && e.name === "AbortError") return
      if (e instanceof Error && e.name === "AbortError") return
      const message = e instanceof Error ? e.message : t("agent.loadError")
      const permissionDenied =
        /\b403\b/.test(message) ||
        /content_access_denied|Insufficient permissions|Недостаточно прав для изменения контента/i.test(message)
      const alreadyToasted = historyLoadErrorToastedKeyRef.current === requestKey
      if (!alreadyToasted) historyLoadErrorToastedKeyRef.current = requestKey
      notifyErrorRef.current(message, { toast: !permissionDenied && !alreadyToasted })
      historyReadyRef.current = true
      setHistoryReady(true)
      if (fadeOutDoneRef.current) setHistoryPaneVisible(true)
    }
  }, [projectId, resolvedAgentScope, mode, contextType, contextId, t, historyContextKey])

  loadHistoryRef.current = loadHistory

  const loadOlderHistory = useCallback(async () => {
    if (!hasOlderRef.current || loadingOlderRef.current) return
    const oldest = oldestPositiveId(messagesRef.current)
    if (oldest == null) return
    const requestKey = historyContextKeyRef.current
    loadingOlderRef.current = true
    olderAbortRef.current?.abort()
    const ac = new AbortController()
    olderAbortRef.current = ac
    const el = historyScrollRef.current
    const prevHeight = el?.scrollHeight ?? 0
    const prevTop = el?.scrollTop ?? 0
    try {
      const data = await fetchAgentHistory({
        projectId,
        agentScope: resolvedAgentScope,
        mode,
        contextType,
        contextId,
        beforeId: oldest,
        signal: ac.signal,
      })
      if (ac.signal.aborted || historyContextKeyRef.current !== requestKey) return
      const older = data.messages.filter((row) => row.role === "user" || row.role === "assistant")
      const known = new Set(messagesRef.current.map((row) => row.id))
      const fresh = older.filter((row) => !known.has(row.id))
      if (fresh.length === 0) {
        hasOlderRef.current = false
        setHasOlder(false)
        return
      }
      setMessages((prev) => {
        const seen = new Set(prev.map((row) => row.id))
        const next = [...fresh.filter((row) => !seen.has(row.id)), ...prev]
        messagesRef.current = next
        return next
      })
      hasOlderRef.current = data.has_older === true
      setHasOlder(hasOlderRef.current)
      if (stickToBottomRef.current) return
      requestAnimationFrame(() => {
        const node = historyScrollRef.current
        if (!node) return
        node.scrollTop = prevTop + (node.scrollHeight - prevHeight)
      })
    } catch (error) {
      if (ac.signal.aborted || historyContextKeyRef.current !== requestKey) return
      if (error instanceof DOMException && error.name === "AbortError") return
      if (error instanceof Error && error.name === "AbortError") return
    } finally {
      if (olderAbortRef.current === ac) loadingOlderRef.current = false
    }
  }, [contextId, contextType, mode, projectId, resolvedAgentScope])

  const clearPollTimer = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }, [])

  const pollRunStateOnce = useCallback(async (): Promise<"ok" | "error" | "skipped"> => {
    if (typeof document !== "undefined" && document.hidden) return "skipped"
    const requestKey = historyContextKey
    pollAbortRef.current?.abort()
    const ac = new AbortController()
    pollAbortRef.current = ac
    try {
      const data: AgentRunStateResponse = await fetchAgentRunState({
        projectId,
        agentScope: resolvedAgentScope,
        mode,
        contextType,
        contextId,
        signal: ac.signal,
      })
      // The in-flight poll still closes over the previous node. Drop it once the panel moved on.
      if (ac.signal.aborted || historyContextKeyRef.current !== requestKey) return "skipped"
      applyRunState(data.run, { lastMessageId: data.last_message_id })
      pollBackoffMsRef.current = RUN_STATE_FALLBACK_MS
      return "ok"
    } catch (error) {
      if (isAbortError(error) || ac.signal.aborted || historyContextKeyRef.current !== requestKey) return "skipped"
      pollBackoffMsRef.current = nextRunStateBackoffMs(pollBackoffMsRef.current)
      return "error"
    }
  }, [applyRunState, contextId, contextType, historyContextKey, mode, projectId, resolvedAgentScope])

  // One timer: live cable + streaming → slow reconcile; cable down → 3s; 5xx → backoff. Never stop while waiting.
  useEffect(() => {
    const needPoll = streaming || !cableConnected
    if (!needPoll) {
      clearPollTimer()
      pollAbortRef.current?.abort()
      return
    }

    let cancelled = false
    const tick = async () => {
      const result = await pollRunStateOnce()
      if (cancelled) return
      const delay = runStatePollDelayMs({
        failed: result === "error",
        backoffMs: pollBackoffMsRef.current,
        cableConnected,
        streaming,
      })
      pollTimerRef.current = setTimeout(() => {
        void tick()
      }, delay)
    }
    void tick()

    const onVisibilityChange = () => {
      if (!document.hidden && !cancelled) void pollRunStateOnce()
    }
    document.addEventListener("visibilitychange", onVisibilityChange)

    return () => {
      cancelled = true
      document.removeEventListener("visibilitychange", onVisibilityChange)
      clearPollTimer()
      pollAbortRef.current?.abort()
    }
  }, [cableConnected, clearPollTimer, pollRunStateOnce, streaming])

  // Only refetch when conversation identity changes — not when applyHistory/notifyError churn.
  useEffect(() => {
    historyAbortRef.current?.abort()
    pendingHistoryRef.current = null
    sendGenRef.current += 1
    sendingRef.current = false
    streamBufferRef.current = ""
    wasProcessingRef.current = false
    runWatchRef.current = null
    lastMessageIdRef.current = null
    lastEventsRef.current = null
    lastAttemptedMessageRef.current = null
    queuedMessageRef.current = null
    networkRetryLoopRef.current = false
    allowQueueDrainRef.current = false
    autoSendConsumedRef.current = false
    autoSendAttemptsRef.current = 0
    resetRunEventLatches()
    setStreaming(false)
    setStreamBuffer("")
    setAgentPhase(null)
    setAgentToolName(null)
    setRequestStartedAt(null)
    setError(null)
    setErrorCode(null)
    setNetworkAutoRetryArmed(false)
    setConfirming(false)
    setStopping(false)
    setQueuedMessage(null)
    // Черновик картинок переключает useAgentComposerImages. Очередь в памяти этой панели.
    queuedImagesRef.current.forEach(releasePendingAgentImage)
    queuedImagesRef.current = []
    setQueueConfirm(null)
    setEditingMessageId(null)
    setEditConfirmOpen(false)
    setPendingAction(null)
    setCanContinue(false)
    revokeBlobPreviewUrls(messagesRef.current)
    messagesRef.current = []
    setMessages([])
    hasOlderRef.current = false
    setHasOlder(false)
    loadingOlderRef.current = false
    olderAbortRef.current?.abort()
    setLastAttemptedMessage(null)
    historyReadyRef.current = false
    setHistoryReady(false)

    const skipFade = firstHistoryKeyRef.current || Boolean(reducedMotionRef.current)
    firstHistoryKeyRef.current = false

    const finishFadeOut = () => {
      fadeOutDoneRef.current = true
      holdHistoryUntilFadeRef.current = false
      const pending = pendingHistoryRef.current
      if (pending) {
        pendingHistoryRef.current = null
        applyHistoryRef.current?.(pending)
        historyReadyRef.current = true
        setHistoryReady(true)
      }
      if (historyReadyRef.current) setHistoryPaneVisible(true)
    }

    if (skipFade) {
      fadeOutDoneRef.current = true
      holdHistoryUntilFadeRef.current = false
      setHistoryPaneVisible(true)
      void loadHistoryRef.current?.()
      return () => {
        historyAbortRef.current?.abort()
      }
    }

    fadeOutDoneRef.current = false
    holdHistoryUntilFadeRef.current = true
    setHistoryPaneVisible(false)
    void loadHistoryRef.current?.()
    const timer = window.setTimeout(finishFadeOut, HISTORY_FADE_MS)
    return () => {
      window.clearTimeout(timer)
      historyAbortRef.current?.abort()
    }
    // reducedMotion via ref so hydrating null→false does not restart the conversation load.
  }, [historyContextKey, resetRunEventLatches])

  const applyRunStateRef = useRef(applyRunState)
  applyRunStateRef.current = applyRunState
  const pollRunStateOnceRef = useRef(pollRunStateOnce)
  pollRunStateOnceRef.current = pollRunStateOnce

  useEffect(() => {
    const subscribedKey = historyContextKeyRef.current
    const consumer = getCableConsumer()
    const companyAgent = resolvedAgentScope.kind === "company"
    const unsubscribe = subscribeCableChannel(
      consumer,
      companyAgent
        ? {
            channel: "Companies::AgentRunChannel",
            company_id: companyId ?? projectId,
            mode,
            context_type: contextType,
            context_id: contextId,
          }
        : {
            channel: "Projects::AgentRunChannel",
            project_id: projectId,
            mode,
            context_type: contextType,
            context_id: contextId,
          },
      {
        connected() {
          setCableConnected(true)
          void pollRunStateOnceRef.current()
        },
        disconnected() {
          setCableConnected(false)
        },
        received(data: unknown) {
          if (historyContextKeyRef.current !== subscribedKey) return
          if (!data || typeof data !== "object" || !("type" in data)) return
          const payload = data as AgentCablePayload
          if (payload.type === "run_delta") {
            wasProcessingRef.current = true
            setStreaming(true)
            const phase = payload.phase === "tool" ? "tool" : "thinking"
            setAgentPhase(phase)
            if (typeof payload.tool_name === "string" && payload.tool_name) {
              setAgentToolName(payload.tool_name)
            } else if (phase !== "tool") {
              setAgentToolName(null)
            }
            const buf = payload.buffer ?? ""
            streamBufferRef.current = buf
            setStreamBuffer(buf)
            return
          }
          if (payload.type === "run") {
            applyRunStateRef.current(payload.run, { lastMessageId: payload.last_message_id })
            return
          }
          if (payload.type === "handoff") {
            const { message_id: messageId, status } = payload.handoff
            if (!messageId || !status) return
            setMessages((prev) =>
              prev.map((m) => (m.id === messageId ? { ...m, meta: { ...(m.meta ?? {}), status } } : m)),
            )
          }
        },
      },
    )

    return () => {
      setCableConnected(false)
      unsubscribe()
    }
    // Channel identity only — applyRunState / pollRunStateOnce go through refs so callback
    // churn does not resubscribe (that raced unsubscribe before confirm).
  }, [companyId, contextId, contextType, mode, projectId, resolvedAgentScope.kind])

  useJobKindFinished("agent_create_shots_batch", () => void loadHistory())

  useEffect(() => {
    if (!expanded || !stickToBottomRef.current) return
    const el = historyScrollRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [messages, streamBuffer, expanded, pendingAction])

  useEffect(() => {
    if (!expanded || !historyReady || !hasOlder) return
    const el = historyScrollRef.current
    if (!el) return
    if (el.scrollTop < 48 || el.scrollHeight - el.clientHeight < 48) void loadOlderHistory()
  }, [expanded, hasOlder, historyReady, loadOlderHistory, messages.length])

  useEffect(() => {
    if (!showQuickCommands) {
      setVisibleQuickCount(0)
      return
    }
    const row = quickRowRef.current
    const measure = measureRowRef.current
    if (!row || !measure) return

    const recompute = () => {
      const gap = 6 // gap-1.5
      const badge = entityBadgeRef.current
      let badgeReserve = 0
      if (badge) {
        const marginRight = Number.parseFloat(getComputedStyle(badge).marginRight) || 0
        badgeReserve = badge.offsetWidth + marginRight + gap
      }
      const available = Math.max(0, row.clientWidth - badgeReserve)
      const chips = Array.from(measure.children) as HTMLElement[]
      if (chips.length === 0) {
        setVisibleQuickCount(0)
        return
      }
      let used = 0
      let count = 0
      for (const chip of chips) {
        const next = used + chip.offsetWidth + (count > 0 ? gap : 0)
        if (next > available + 0.5) break
        used = next
        count += 1
      }
      setVisibleQuickCount(count)
    }

    recompute()
    const ro = new ResizeObserver(recompute)
    ro.observe(row)
    if (entityBadgeRef.current) ro.observe(entityBadgeRef.current)
    return () => ro.disconnect()
  }, [t, showQuickCommands, quickCommands, selectedEntity])

  useEffect(() => {
    if (!expanded || !collapseOnOutsideClick) return
    const onPointerDown = (event: PointerEvent) => {
      const root = panelRef.current
      const target = event.target
      if (!(target instanceof Node) || !root) return
      if (root.contains(target)) return
      // Plus / slash menus portal outside panelRef (body or dialog host).
      if (target instanceof Element && target.closest("[data-agent-portal-menu], [role='menu'], [role='dialog']"))
        return
      setExpanded(false)
    }
    document.addEventListener("pointerdown", onPointerDown, true)
    return () => document.removeEventListener("pointerdown", onPointerDown, true)
  }, [expanded, collapseOnOutsideClick, setExpanded])

  // Общий старт хода для новой отправки и переотправки: сброс ошибок, стрима и защёлок событий прогона.
  const beginTurn = useCallback(
    (message: string, fromNetworkAutoRetry: boolean) => {
      const sendGen = ++sendGenRef.current
      const requestKey = historyContextKeyRef.current
      sendingRef.current = true
      lastAttemptedMessageRef.current = message
      setLastAttemptedMessage(message)
      allowQueueDrainRef.current = false
      if (!fromNetworkAutoRetry) {
        networkRetryLoopRef.current = false
        setNetworkAutoRetryArmed(false)
      }

      setError(null)
      setErrorCode(null)
      setCanContinue(false)
      setEditingMessageId(null)
      composerRef.current?.clear()
      setExpanded(true)
      setStreaming(true)
      setStopping(false)
      setStreamBuffer("")
      streamBufferRef.current = ""
      setAgentPhase("thinking")
      setAgentToolName(null)
      setRequestStartedAt(new Date().toISOString())
      resetRunEventLatches()
      runWatchRef.current = awaitingClaimWatch()
      wasProcessingRef.current = true
      stickToBottomRef.current = true
      return { sendGen, requestKey }
    },
    [resetRunEventLatches, setExpanded],
  )

  const sendMessage = useCallback(
    async (
      text: string,
      options?: { fromNetworkAutoRetry?: boolean; images?: PendingAgentImage[] },
    ): Promise<boolean> => {
      const epoch = epochRef.current
      if (options?.images == null) {
        await whenImagesReady()
        if (epochRef.current !== epoch) return false
      }
      const message = text.trim()
      const images = options?.images ?? pendingImagesRef.current
      const fromNetworkAutoRetry = options?.fromNetworkAutoRetry === true
      // Allow send while cancel is in flight so the user is never blocked after Stop.
      if ((!message && images.length === 0) || (streaming && !stopping)) return false
      if (sendingRef.current && !stopping) return false

      const retryBusyAfterStop = stopping
      const { sendGen, requestKey } = beginTurn(message, fromNetworkAutoRetry)
      lastAttemptedImagesRef.current = images
      const { files, sources, imageOrder } = pendingAgentImagesPayload(images)
      setPendingImages([])
      const optimisticId = -Date.now()
      const clientKey = `optimistic-user-${optimisticId}`
      const localImages = optimisticAgentImages(images)
      setMessages((prev) => [
        ...prev,
        {
          id: optimisticId,
          clientKey,
          role: "user",
          content: message,
          images: localImages,
          author: currentUserEmail
            ? {
                email: currentUserEmail,
                first_name: currentUser?.first_name,
                last_name: currentUser?.last_name,
              }
            : undefined,
          created_at: new Date().toISOString(),
        },
      ])

      let ok = false
      try {
        const chatParams = {
          projectId,
          agentScope: resolvedAgentScope,
          message,
          mode,
          contextType,
          contextId,
          selectedShotId,
          selectedEntityId,
          episodeId,
          canvasDraft,
          scriptFocus,
          selectedTaskId,
          scene3dFocus: getScene3dFocus?.() ?? null,
          images: files,
          sources,
          imageOrder,
        }
        const deadline = Date.now() + BUSY_RETRY_BUDGET_MS
        let chatData: Awaited<ReturnType<typeof postAgentChat>> | null = null
        for (;;) {
          try {
            chatData = await postAgentChat(chatParams)
            break
          } catch (e) {
            const err = e as Error & { errorCode?: string }
            if (retryBusyAfterStop && err.errorCode === "busy" && Date.now() < deadline) {
              await sleep(BUSY_RETRY_MS)
              continue
            }
            throw e
          }
        }
        if (chatData?.user_message && chatData.user_message.id > 0) {
          const serverMsg = {
            ...chatData.user_message,
            clientKey,
            images: withLocalPreviewUrls(chatData.user_message.images, localImages),
          }
          setMessages((prev) => {
            const withoutOptimistic = prev.filter((m) => m.id !== optimisticId && m.clientKey !== clientKey)
            if (withoutOptimistic.some((m) => m.id === serverMsg.id)) {
              return withoutOptimistic.map((m) =>
                m.id === serverMsg.id
                  ? {
                      ...serverMsg,
                      clientKey: m.clientKey ?? clientKey,
                      images: withLocalPreviewUrls(serverMsg.images ?? m.images, localImages) ?? m.images,
                    }
                  : m,
              )
            }
            return [...withoutOptimistic, serverMsg]
          })
          if (typeof chatData.last_message_id === "number") {
            lastMessageIdRef.current = chatData.last_message_id
          } else {
            lastMessageIdRef.current = serverMsg.id
          }
        }
        if (historyContextKeyRef.current !== requestKey) return false
        if (chatData?.run) {
          applyRunState(chatData.run, {
            lastMessageId: chatData.last_message_id ?? chatData.user_message?.id ?? null,
          })
        }
        await pollRunStateOnce()
        ok = true
      } catch (e) {
        const err = e as Error & { errorCode?: string }
        // Иначе loadHistory увидит awaitingClaim и оставит панель в ожидании прогона.
        runWatchRef.current = null
        wasProcessingRef.current = false
        if (fromNetworkAutoRetry && err.errorCode === "busy") {
          try {
            await pollRunStateOnce()
          } catch {
            setStreaming(false)
            setStopping(false)
            setAgentPhase(null)
            setAgentToolName(null)
            setRequestStartedAt(null)
            streamBufferRef.current = ""
            setStreamBuffer("")
          }
          try {
            await loadHistory()
          } catch {
            // ignore
          }
        } else {
          setStreaming(false)
          setStopping(false)
          setAgentPhase(null)
          setAgentToolName(null)
          setRequestStartedAt(null)
          streamBufferRef.current = ""
          setStreamBuffer("")
          if (err.errorCode === "busy") {
            notifyError(t("agent.busy"), { code: "busy" })
          } else {
            notifyError(e instanceof Error ? e.message : t("agent.error"))
          }
          try {
            await loadHistory()
          } catch {
            // ignore
          }
          setInput(message)
          setPendingImages(images)
        }
      } finally {
        if (sendGenRef.current === sendGen) sendingRef.current = false
      }
      if (ok) setQueueKick((n) => n + 1)
      return ok
    },
    [
      streaming,
      stopping,
      projectId,
      resolvedAgentScope,
      mode,
      contextType,
      contextId,
      selectedShotId,
      selectedEntityId,
      canvasDraft,
      getScene3dFocus,
      scriptFocus,
      selectedTaskId,
      episodeId,
      applyRunState,
      beginTurn,
      pollRunStateOnce,
      loadHistory,
      notifyError,
      setInput,
      t,
      currentUser,
      currentUserEmail,
      epochRef,
      pendingImagesRef,
      setPendingImages,
      whenImagesReady,
    ],
  )

  const resendExistingUserTurn = useCallback(
    async (
      messageId: number,
      text: string,
      options?: { fromNetworkAutoRetry?: boolean; restoreEditingOnError?: boolean; allowBlank?: boolean },
    ): Promise<boolean> => {
      const message = text.trim()
      const fromNetworkAutoRetry = options?.fromNetworkAutoRetry === true
      if (messageId <= 0 || (streaming && !stopping)) return false
      if (!message && !options?.allowBlank) return false
      if (sendingRef.current && !stopping) return false

      const { sendGen, requestKey } = beginTurn(message, fromNetworkAutoRetry)
      setMessages((prev) => {
        const dropped = prev.filter((item) => item.id <= 0 || item.id > messageId)
        revokeBlobPreviewUrls(dropped)
        return prev.filter((item) => item.id > 0 && item.id <= messageId)
      })

      let ok = false
      try {
        const chatData = await postAgentEditAndResend({
          projectId,
          agentScope: resolvedAgentScope,
          messageId,
          message,
          mode,
          contextType,
          contextId,
          selectedShotId,
          selectedEntityId,
          episodeId,
          canvasDraft,
          scriptFocus,
          selectedTaskId,
          scene3dFocus: getScene3dFocus?.() ?? null,
        })
        if (historyContextKeyRef.current !== requestKey) return false
        if (chatData?.run) applyRunState(chatData.run)
        await loadHistory()
        await pollRunStateOnce()
        ok = true
      } catch (e) {
        const err = e as Error & { errorCode?: string }
        runWatchRef.current = null
        wasProcessingRef.current = false
        if (fromNetworkAutoRetry && err.errorCode === "busy") {
          try {
            await pollRunStateOnce()
          } catch {
            setStreaming(false)
            setStopping(false)
            setAgentPhase(null)
            setAgentToolName(null)
            setRequestStartedAt(null)
            streamBufferRef.current = ""
            setStreamBuffer("")
          }
          try {
            await loadHistory()
          } catch {
            // ignore
          }
        } else {
          setStreaming(false)
          setStopping(false)
          setAgentPhase(null)
          setAgentToolName(null)
          setRequestStartedAt(null)
          streamBufferRef.current = ""
          setStreamBuffer("")
          if (err.errorCode === "busy") {
            notifyError(t("agent.busy"), { code: "busy" })
          } else {
            notifyError(e instanceof Error ? e.message : t("agent.error"))
          }
          try {
            await loadHistory()
          } catch {
            // ignore
          }
          if (options?.restoreEditingOnError) {
            setInput(message)
            setEditingMessageId(messageId)
          }
        }
      } finally {
        if (sendGenRef.current === sendGen) sendingRef.current = false
      }
      if (ok) setQueueKick((n) => n + 1)
      return ok
    },
    [
      applyRunState,
      beginTurn,
      canvasDraft,
      getScene3dFocus,
      scriptFocus,
      selectedTaskId,
      contextId,
      contextType,
      episodeId,
      loadHistory,
      notifyError,
      mode,
      pollRunStateOnce,
      projectId,
      resolvedAgentScope,
      selectedEntityId,
      selectedShotId,
      setInput,
      stopping,
      streaming,
      t,
    ],
  )

  const retryLastTurn = useCallback(
    async (options?: { fromNetworkAutoRetry?: boolean }): Promise<boolean> => {
      const last = findLastRetryableUserMessage(messages, currentUserEmail, isSuperAdmin)
      if (last && last.id > 0) {
        const text = last.content?.trim() || ""
        const hasImages = (last.images?.length ?? 0) > 0
        if (!text && !hasImages) return false
        return resendExistingUserTurn(last.id, text, { ...options, allowBlank: !text && hasImages })
      }
      const fallback = lastAttemptedMessageRef.current?.trim() || lastAttemptedMessage?.trim() || ""
      const images = lastAttemptedImagesRef.current
      if (!fallback && images.length === 0) return false
      return sendMessage(fallback, { ...options, images })
    },
    [currentUserEmail, isSuperAdmin, lastAttemptedMessage, messages, resendExistingUserTurn, sendMessage],
  )

  const requestSend = useCallback(
    (text: string) => {
      void (async () => {
        const epoch = epochRef.current
        await whenImagesReady()
        if (epochRef.current !== epoch) return
        const message = text.trim()
        const images = pendingImagesRef.current
        if ((!message && images.length === 0) || stopping) return
        if (editingMessageId != null) {
          if (!message || streaming) return
          setEditConfirmOpen(true)
          return
        }
        if (streaming) {
          setQueueConfirm({
            text: message,
            replace: Boolean(queuedMessageRef.current) || queuedImagesRef.current.length > 0,
          })
          return
        }
        void sendMessage(message, { images })
      })()
    },
    [editingMessageId, epochRef, pendingImagesRef, sendMessage, stopping, streaming, whenImagesReady],
  )

  const confirmQueue = useCallback(() => {
    if (!queueConfirm) return
    const text = queueConfirm.text
    void (async () => {
      const epoch = epochRef.current
      await whenImagesReady()
      if (epochRef.current !== epoch) return
      const images = pendingImagesRef.current
      queuedImagesRef.current.forEach(releasePendingAgentImage)
      composerRef.current?.clear()
      queuedMessageRef.current = text
      queuedImagesRef.current = images
      setPendingImages([])
      setQueuedMessage(
        text ||
          pendingAgentQueueLabel(images, {
            image: t("agent.imageOnlyQueued"),
            video: t("agent.videoOnlyQueued"),
            mixed: t("agent.attachmentOnlyQueued"),
          }),
      )
      setQueueConfirm(null)
    })()
  }, [epochRef, pendingImagesRef, queueConfirm, setPendingImages, t, whenImagesReady])

  const clearQueuedMessage = useCallback(() => {
    queuedMessageRef.current = null
    queuedImagesRef.current.forEach(releasePendingAgentImage)
    queuedImagesRef.current = []
    setQueuedMessage(null)
  }, [])

  useEffect(() => {
    if (streaming || stopping || sendingRef.current) return
    if (!allowQueueDrainRef.current) return
    const next = queuedMessageRef.current
    const images = queuedImagesRef.current
    if (next == null && images.length === 0) return
    queuedMessageRef.current = null
    queuedImagesRef.current = []
    setQueuedMessage(null)
    allowQueueDrainRef.current = false
    void sendMessage(next ?? "", { images })
  }, [sendMessage, stopping, streaming, queueKick])

  const submitEditedMessage = useCallback(async () => {
    const message = (composerRef.current?.getText() ?? input).trim()
    const messageId = editingMessageId
    if (!message || messageId == null || streaming || stopping) return
    setEditConfirmOpen(false)
    await resendExistingUserTurn(messageId, message, { restoreEditingOnError: true })
  }, [editingMessageId, input, resendExistingUserTurn, stopping, streaming])

  useEffect(() => {
    autoSendAliveRef.current = true
    return () => {
      autoSendAliveRef.current = false
    }
  }, [])

  useEffect(() => {
    autoSendConsumedRef.current = false
    autoSendAttemptsRef.current = 0
  }, [autoSendMessage])

  useEffect(() => {
    const message = autoSendMessage?.trim()
    if (!canEditContent) return
    if (!message || !historyReady || streaming || stopping || sendingRef.current || autoSendConsumedRef.current) return
    if (queuedMessageRef.current) return
    if (autoSendAttemptsRef.current >= AUTO_SEND_MAX_ATTEMPTS) {
      onAutoSendSettled?.()
      return
    }
    autoSendConsumedRef.current = true
    autoSendAttemptsRef.current += 1
    const attempt = autoSendAttemptsRef.current
    void sendMessage(message).then(async (ok) => {
      if (!autoSendAliveRef.current) return
      const decision = decideAutoSendAfterAttempt({ ok, attempt })
      if (decision.action === "settle") {
        onAutoSendSettled?.()
        return
      }
      if (decision.action === "retry_after_ms") {
        await sleep(decision.ms)
        if (!autoSendAliveRef.current) return
        autoSendConsumedRef.current = false
        setAutoSendRetryTick((n) => n + 1)
      }
    })
  }, [
    autoSendMessage,
    autoSendRetryTick,
    historyReady,
    streaming,
    stopping,
    sendMessage,
    onAutoSendSettled,
    canEditContent,
  ])

  const handleSelectSessionSkill = useCallback((skill: AgentSessionSkill) => {
    composerRef.current?.insertSkill(skill.slug)
    composerRef.current?.focus()
  }, [])

  const handleStop = async () => {
    if (!streaming || stopping) return
    setStopping(true)
    try {
      await cancelAgentRun({ projectId, agentScope: resolvedAgentScope, mode, contextType, contextId })
    } catch (e) {
      setStopping(false)
      notifyError(e instanceof Error ? e.message : t("agent.error"))
    }
  }

  const handleConfirm = async () => {
    if (!pendingAction || confirming) return
    const action = pendingAction
    setConfirming(true)
    setError(null)
    setErrorCode(null)
    try {
      const draftPatch = lastEventsRef.current?.canvas_draft_updated
      if (draftPatch) {
        canvasDraftNotifyRef.current = true
        onCanvasDraftUpdated?.(draftPatch)
      }
      const res = await confirmAgentAction({
        projectId,
        agentScope: resolvedAgentScope,
        mode,
        contextType,
        contextId,
        episodeId,
        selectedShotId,
        selectedEntityId,
        canvasDraft: mergeCanvasDraftForConfirm(canvasDraft, draftPatch) ?? canvasDraft,
        scene3dFocus: getScene3dFocus?.() ?? null,
        pendingAction: action,
      })
      if (!res.ok) throw new Error(res.error || t("agent.confirmError"))
      if (res.scene3d_layout_updated) onScene3dLayoutUpdated?.(res.scene3d_layout_updated)
      const confirmResult = res.result
      if (confirmResult?.flow_changed) {
        flowBoardNotifyRef.current = true
        onFlowBoardChanged?.()
      }
      if (action.tool_name === "write_notes" && Array.isArray(confirmResult?.notes)) {
        notifyNotesChanged()
      }
      if (confirmResult && typeof confirmResult.entity_type === "string") {
        notifyEntityUpdated(
          {
            entity_type: confirmResult.entity_type,
            id: typeof confirmResult.id === "number" ? confirmResult.id : null,
            name: typeof confirmResult.name === "string" ? confirmResult.name : "",
            profile_text: typeof confirmResult.profile_text === "string" ? confirmResult.profile_text : null,
            deleted: confirmResult.deleted === true,
          },
          onEntityUpdated,
        )
      }
      onAgentActionConfirmed?.(res, action)
      setPendingAction(null)
      await loadHistory()
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t("agent.confirmError"))
    } finally {
      setConfirming(false)
    }
  }

  const handleCancel = async () => {
    setConfirming(true)
    try {
      await cancelAgentAction({ projectId, agentScope: resolvedAgentScope, mode, contextType, contextId })
      setPendingAction(null)
      await loadHistory()
    } finally {
      setConfirming(false)
    }
  }

  const visibleMessages = messages.filter(
    (m) =>
      m.role === "user" || (m.role === "assistant" && (Boolean(m.content) || parseSavedPromptCards(m.meta).length > 0)),
  )
  const isEmpty = visibleMessages.length === 0 && !streaming && !pendingAction
  const composerLocked = stopping || !canEditContent
  const imageAttachEnabled = !composerLocked && !bodySlotVisible && imagesReady
  const acceptAgentImageFiles = useCallback(
    (files: readonly File[]) => {
      setPendingImages((prev) =>
        appendPendingAgentImages(prev, files, (message) => toast.error(message), {
          limit: t("agent.imageLimit"),
          videoLimit: t("agent.videoLimit"),
          badType: t("agent.imageBadType"),
          tooLarge: t("agent.imageTooLarge"),
          videoTooLarge: t("agent.videoTooLarge"),
        }),
      )
    },
    [setPendingImages, t],
  )
  // Capture, а не bubble: TipTap глушит вставку картинки до того, как событие всплывёт.
  useEffect(() => {
    const root = panelRef.current
    if (!imageAttachEnabled || !root) return
    const onPaste = (event: ClipboardEvent) => {
      if (event.defaultPrevented) return
      if (!clipboardHasFiles(event.clipboardData)) return
      const files = filesFromClipboard(event.clipboardData)
      if (files.length === 0) return
      event.preventDefault()
      event.stopPropagation()
      void normalizePastedFiles(files).then(acceptAgentImageFiles)
    }
    root.addEventListener("paste", onPaste, true)
    return () => root.removeEventListener("paste", onPaste, true)
  }, [acceptAgentImageFiles, imageAttachEnabled])
  const thinkingPhrasesRaw = t("agent.thinkingPhrases", { returnObjects: true })
  const thinkingPhrases = useMemo(() => {
    return Array.isArray(thinkingPhrasesRaw)
      ? thinkingPhrasesRaw.filter((p): p is string => typeof p === "string" && p.trim() !== "")
      : [t("agent.thinking")]
  }, [t, thinkingPhrasesRaw])
  const rotatingPhrase = useRotatingPhrase(
    thinkingPhrases,
    7000,
    Boolean(streaming && !stopping && agentPhase !== "tool"),
  )
  const statusLabel = stopping
    ? t("agent.stopping")
    : agentPhase === "tool"
      ? t(`agent.toolStatus.${agentToolName ?? "unknown"}`, { defaultValue: t("agent.workingTool") })
      : rotatingPhrase
  const lastRetryableUserMessage = findLastRetryableUserMessage(visibleMessages, currentUserEmail, isSuperAdmin)
  const retryText = lastAttemptedMessage?.trim() || lastRetryableUserMessage?.content?.trim() || ""
  const canRetryTurn = retryText.length > 0 || (lastRetryableUserMessage?.images?.length ?? 0) > 0
  const lastVisibleMessage = visibleMessages[visibleMessages.length - 1]
  const orphanLastUserTurn = lastVisibleMessage?.role === "user" && lastVisibleMessage.id > 0
  const showAdminOrphanRetry = isSuperAdmin && orphanLastUserTurn && canRetryTurn && !streaming && !stopping
  const networkRetryLockKey = `agent-network-retry:${projectId}:${mode}:${contextType}:${contextId}`
  const networkRetrySecondsLeft = useAgentNetworkAutoRetry({
    active:
      Boolean(error) &&
      errorCode === "network" &&
      networkAutoRetryArmed &&
      !streaming &&
      !stopping &&
      retryText.length > 0,
    retryText,
    lockKey: networkRetryLockKey,
    onRetry: () => {
      networkRetryLoopRef.current = true
      void retryLastTurn({ fromNetworkAutoRetry: true })
    },
  })
  const showSkillsPicker = expanded && availableSkills.length > 0
  const showShotLabel = Boolean(shotLabel) && (contextType === "shot" || Boolean(selectedShotId))
  const projectLabel = projectName?.trim() || t("agent.untitledProject")
  const {
    boxRef: composerBoxRef,
    contentRef: composerContentRef,
    animating: composerResizing,
    onTransitionEnd: onComposerResizeEnd,
  } = useSwapHeightAnimation(pinComposerSlot ? "pinned" : composerSlot ? "slot" : "composer")
  const pinSlot = pinComposerSlot && Boolean(composerSlot)
  const slotVisible = pinSlot && composerSlotVisible
  const composerPillOverlap = Boolean(composerEdgeOverlay)
  const composerTopBanner = !canEditContent || editingMessageId != null || Boolean(queuedMessage)

  return (
    <div
      className={cn(
        "pointer-events-none flex",
        zIndexClass,
        anchorInline
          ? cn("relative w-full min-w-0", fillAvailableHeight && "h-full min-h-0")
          : cn(
              "bottom-0",
              anchorParent
                ? "absolute inset-x-0"
                : cn(
                    "fixed justify-center px-3 pb-3 md:px-4 lg:px-6",
                    !overlay &&
                      (sidebarCollapsed ? "left-0 md:left-14" : "left-0 md:left-[var(--shell-sidebar-width)]"),
                    overlay && "inset-x-0",
                  ),
            ),
        className,
      )}
      style={overlay || anchorParent || anchorInline ? undefined : { right: drawerContentRight }}
    >
      <div
        ref={panelRef}
        data-agent-chat-panel
        className={cn(
          "pointer-events-auto flex w-full min-w-0 flex-col gap-2",
          anchorParent || anchorInline ? "max-w-none" : "mx-auto max-w-none md:max-w-4xl",
          fillAvailableHeight && "h-full min-h-0",
        )}
      >
        {toolbar ? (
          <div className="flex flex-wrap items-center justify-end gap-2 [&_button.border]:bg-ui-bg-elevated dark:[&_button.border]:hover:bg-zinc-800 light:[&_button.border]:hover:bg-clay-2">
            {toolbar}
          </div>
        ) : null}
        <div
          className={cn(
            chromeless ? "relative flex min-h-0 min-w-0 flex-1 flex-col overflow-visible p-3" : PANEL_CARD_CLASS,
            !chromeless && (anchorInline ? "pt-5" : "pt-3"),
            fillAvailableHeight && "flex min-h-0 flex-1 flex-col",
          )}
        >
          <div
            className={cn(
              "grid grid-rows-[1fr]",
              fillAvailableHeight && expanded && "min-h-0 flex-1",
              bodySlot && "relative",
            )}
          >
            <div
              className={cn(
                "min-h-0 overflow-hidden",
                fillAvailableHeight && expanded && "flex min-h-0 flex-col",
                bodySlot && "transition-opacity duration-200",
                bodySlotVisible && "pointer-events-none opacity-0",
              )}
              {...(bodySlotVisible ? { inert: true } : {})}
            >
              {!hideTitle ? (
                <div className="mb-2 flex shrink-0 items-center gap-2 px-0.5">
                  <Sparkles className="h-3.5 w-3.5 text-ui-accent" />
                  <span className="text-xs font-medium text-ui-text-primary">
                    {title ?? t(chatAgentLabelKey(mode, contextType))}
                  </span>
                  {showShotLabel ? (
                    <span className="text-[11px] text-ui-text-secondary">
                      {t("agent.shotContext", { label: shotLabel })}
                    </span>
                  ) : null}
                  {showExpandToggle ? (
                    <button
                      type="button"
                      className="ml-auto text-[11px] text-ui-text-secondary hover:text-ui-text-primary"
                      onClick={() => setExpanded((v) => !v)}
                    >
                      {expanded ? t("agent.collapse") : t("agent.expand")}
                    </button>
                  ) : null}
                </div>
              ) : null}

              {contextStrip && expanded ? <div className="mb-2 shrink-0">{contextStrip}</div> : null}

              <div
                className={cn(
                  "grid transition-[grid-template-rows] duration-120 ease-linear",
                  expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                  fillAvailableHeight && expanded && "min-h-0 flex-1",
                )}
                aria-hidden={!expanded}
                {...(!expanded ? { inert: true } : {})}
              >
                <div
                  className={cn(
                    "min-h-0 overflow-hidden transition-opacity duration-120 ease-linear",
                    expanded ? "opacity-100" : "opacity-0",
                    fillAvailableHeight && expanded && "flex min-h-0 flex-col",
                  )}
                >
                  <div
                    className={cn(
                      "min-h-0",
                      fillAvailableHeight && expanded && "flex min-h-0 flex-1 flex-col",
                      !reducedMotion && "transition-opacity duration-120 ease-out",
                      historyPaneVisible ? "opacity-100" : "pointer-events-none opacity-0",
                    )}
                  >
                    {isEmpty ? (
                      <div
                        className={cn(
                          "mb-2 rounded-lg border border-dashed border-ui-border/80 bg-ui-surface-sunken/30 px-4 py-5 text-center",
                          fillAvailableHeight &&
                            expanded &&
                            "flex min-h-[10%] flex-1 flex-col items-center justify-center",
                        )}
                      >
                        <p className="text-xs font-medium text-ui-text-primary">
                          {projectsHomeSurface
                            ? t("agent.emptyCompanyTitle", { company: projectLabel })
                            : (contextType === "flow" || contextType === "flow_node") && title
                              ? title
                              : t("agent.emptyTitle", { project: projectLabel })}
                        </p>
                        {shotLabel ? (
                          <p className="mt-1 text-[11px] text-ui-text-secondary">
                            {t("agent.emptyShot", { label: shotLabel })}
                          </p>
                        ) : null}
                        <p className="mt-2 text-[11px] text-ui-text-muted">
                          {projectsHomeSurface ? t("agent.emptyCompanyHint") : t("agent.emptyHint")}
                        </p>
                      </div>
                    ) : (
                      <div
                        ref={historyScrollRef}
                        data-panel-wheel-isolate
                        className={cn(
                          "mb-2 space-y-2 overflow-y-auto rounded-lg border border-ui-border bg-ui-surface-sunken/40 p-2",
                          fillAvailableHeight && expanded ? "min-h-[10%] flex-1" : "max-h-[40vh]",
                        )}
                        onScroll={(e) => {
                          const el = e.currentTarget
                          const distance = el.scrollHeight - el.scrollTop - el.clientHeight
                          stickToBottomRef.current = distance < 48
                          if (el.scrollTop < 48) void loadOlderHistory()
                        }}
                      >
                        {visibleMessages.map((m) => {
                          const ownUser =
                            m.role === "user" && m.id > 0 && (!m.author?.email || m.author.email === currentUserEmail)
                          const durationMs =
                            m.role === "assistant" && typeof m.meta?.duration_ms === "number"
                              ? m.meta.duration_ms
                              : null
                          const authorLabel =
                            m.role === "user" && m.author?.email
                              ? personDisplayName({
                                  email: m.author.email,
                                  first_name: m.author.first_name,
                                  last_name: m.author.last_name,
                                })
                              : null
                          const timeLabel = m.role === "user" ? formatAgentMessageStamp(m.created_at, locale) : null
                          const handoffMeta = m.role === "assistant" ? parseHandoffMeta(m.meta) : null
                          const savedPrompts = m.role === "assistant" ? parseSavedPromptCards(m.meta) : []
                          if (handoffMeta) {
                            return (
                              <div key={messageListKey(m)} className="!mt-1 flex justify-start">
                                <AgentHandoffChip projectId={projectId} meta={handoffMeta} startedAt={m.created_at} />
                              </div>
                            )
                          }
                          return (
                            <div
                              key={messageListKey(m)}
                              className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}
                            >
                              <div
                                className={cn(
                                  "group relative max-w-[calc(100%-1.5rem)] rounded-lg px-2.5 py-1.5 text-xs",
                                  m.role === "user"
                                    ? "min-w-0 bg-ui-accent/15 text-ui-text-primary"
                                    : "w-fit bg-ui-surface text-ui-text-primary",
                                )}
                              >
                                {m.role === "assistant" && m.content ? (
                                  <div className="leading-relaxed">
                                    <AgentMarkdown content={m.content} shotRefs={shotRefs} onOpenShot={onOpenShot} />
                                  </div>
                                ) : m.role === "user" ? (
                                  <>
                                    {m.images && m.images.length > 0 ? (
                                      <AgentMessageImages
                                        images={m.images}
                                        failed={m.meta?.vision_status === "failed"}
                                      />
                                    ) : null}
                                    {m.content ? (
                                      <AgentNodeMentionText
                                        text={m.content}
                                        items={[
                                          ...agentImageMentionItems(
                                            (m.images ?? []).map((image) => ({
                                              mediaType: image.content_type?.startsWith("video/")
                                                ? ("video" as const)
                                                : ("image" as const),
                                              previewUrl: image.content_type?.startsWith("video/")
                                                ? null
                                                : image.thumb_url || image.download_url || null,
                                            })),
                                          ),
                                          ...(nodeMentionItems ?? []),
                                        ]}
                                      />
                                    ) : null}
                                  </>
                                ) : null}
                                {savedPrompts.length > 0 ? (
                                  <AgentSavedPromptBlock
                                    prompts={savedPrompts}
                                    mentionItems={[...(mentionItems ?? []), ...(nodeMentionItems ?? [])]}
                                  />
                                ) : null}
                                {authorLabel || timeLabel ? (
                                  <div className="mt-0.5 flex min-w-0 items-baseline justify-end gap-1 text-[10px] leading-none text-ui-text-muted">
                                    {authorLabel ? <span className="min-w-0 truncate">{authorLabel}</span> : null}
                                    {timeLabel ? (
                                      <span className="shrink-0 whitespace-nowrap">
                                        {authorLabel ? `· ${timeLabel}` : timeLabel}
                                      </span>
                                    ) : null}
                                  </div>
                                ) : null}
                                {typeof durationMs === "number" && durationMs >= 0 ? (
                                  <div className="mt-1 text-[10px] text-ui-text-muted tabular-nums">
                                    {formatElapsed(durationMs)}
                                  </div>
                                ) : null}
                                {ownUser && !streaming ? (
                                  <div className="absolute -top-1.5 -left-1.5 hidden h-5 w-5 group-hover:block">
                                    <ActionHintTooltip label={t("agent.editMessage")}>
                                      <button
                                        type="button"
                                        className="flex h-5 w-5 items-center justify-center rounded-full border border-ui-border bg-ui-surface text-ui-text-muted hover:text-ui-text-primary"
                                        aria-label={t("agent.editMessage")}
                                        onClick={() => {
                                          const text = m.content?.toString() ?? ""
                                          setEditingMessageId(m.id)
                                          setInput(text)
                                          setExpanded(true)
                                          onQuickCommandUsed?.()
                                        }}
                                      >
                                        <Pencil className="h-3 w-3" />
                                      </button>
                                    </ActionHintTooltip>
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          )
                        })}
                        {streaming && streamBuffer ? (
                          <div className="flex justify-start">
                            <div className="w-fit max-w-[calc(100%-1.5rem)] rounded-lg bg-ui-surface px-2.5 py-1.5 text-xs leading-relaxed text-ui-text-primary">
                              <AgentMarkdown
                                content={streamBuffer}
                                className={STREAM_CURSOR_CLASS}
                                shotRefs={shotRefs}
                                onOpenShot={onOpenShot}
                              />
                            </div>
                          </div>
                        ) : null}
                        {streaming ? (
                          <div className="flex items-center gap-2 px-1 text-[11px] text-ui-text-secondary">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            <span>{statusLabel}</span>
                            {requestStartedAt ? (
                              <JobElapsedTimer startedAt={requestStartedAt} active={streaming} />
                            ) : null}
                            <ActionHintTooltip label={t("agent.stop")}>
                              <Button
                                type="button"
                                size="sm"
                                variant="secondary"
                                className="ml-auto h-6 w-6 p-0"
                                disabled={stopping}
                                onClick={() => void handleStop()}
                                aria-label={t("agent.stop")}
                              >
                                <Square className="h-3 w-3 fill-current" />
                              </Button>
                            </ActionHintTooltip>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {pendingAction ? (
                <div className="mb-2 shrink-0 rounded-lg border border-ui-border bg-ui-surface p-2.5">
                  <div className="text-xs text-ui-text-primary">
                    <AgentMarkdown
                      content={pendingAction.human_readable_summary}
                      shotRefs={shotRefs}
                      onOpenShot={onOpenShot}
                    />
                  </div>
                  <div className="mt-2 flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => void handleConfirm()}
                      disabled={confirming || !canEditContent}
                    >
                      {confirming ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                      {t("agent.execute")}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => void handleCancel()}
                      disabled={confirming || !canEditContent}
                    >
                      {t("agent.cancel")}
                    </Button>
                  </div>
                </div>
              ) : null}

              {/* Событие ещё и тостом; здесь текст живёт, пока не закроют: иначе
                  уже упавший прогон и срыв истории остаются кнопками без причины.
                  Суперадмин: тот же Retry, если чат завис на последнем user-ходе без ответа. */}
              {error || showAdminOrphanRetry ? (
                <div className="mb-2 flex shrink-0 flex-wrap items-center gap-2">
                  {error ? (
                    <p className="text-[11px] text-red-400" role="alert">
                      {error}
                      {networkRetrySecondsLeft != null ? (
                        <span className="ml-1 text-red-400/70">
                          {t("agent.networkRetryIn", { seconds: networkRetrySecondsLeft })}
                        </span>
                      ) : null}
                    </p>
                  ) : null}
                  {error && canContinue ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      className="h-7 text-[11px]"
                      disabled={streaming}
                      onClick={() => void sendMessage(t("agent.continuePhrase"))}
                    >
                      {t("agent.continue")}
                    </Button>
                  ) : null}
                  {canRetryTurn ? (
                    <ActionHintTooltip label={isSuperAdmin ? t("agent.silentRetryHint") : undefined}>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        className="h-7 text-[11px]"
                        disabled={streaming || stopping}
                        onClick={() => void retryLastTurn()}
                      >
                        <RotateCcw className="h-3 w-3" />
                        {t("agent.retry")}
                      </Button>
                    </ActionHintTooltip>
                  ) : null}
                </div>
              ) : null}

              {showQuickRow ? (
                <div
                  className={cn(
                    "grid transition-[grid-template-rows] duration-120 ease-linear",
                    expanded ? "mb-2 grid-rows-[1fr]" : "grid-rows-[0fr]",
                  )}
                  aria-hidden={!expanded}
                  {...(!expanded ? { inert: true } : {})}
                >
                  <div
                    className={cn(
                      "min-h-0 overflow-hidden transition-opacity duration-120 ease-linear",
                      expanded ? "opacity-100" : "opacity-0",
                    )}
                  >
                    <div
                      ref={quickRowRef}
                      className={cn(
                        "relative flex min-h-7 gap-1.5",
                        quickActionsSlot
                          ? "w-full flex-wrap items-center overflow-visible"
                          : "items-center overflow-hidden",
                      )}
                    >
                      {selectedEntity ? (
                        <div ref={entityBadgeRef} className="mr-1 flex shrink-0 items-center gap-1.5">
                          <EntityThumbSquare
                            name={selectedEntity.name}
                            imageUrl={selectedEntity.imageUrl}
                            fallbackIcon={selectedEntity.fallbackIcon}
                            size="radio"
                            className="h-6 w-6"
                          />
                          <span className="max-w-[8rem] truncate text-[11px] font-medium text-ui-text-primary">
                            {selectedEntity.name}
                          </span>
                        </div>
                      ) : null}
                      {quickActionsSlot ? quickActionsSlot : null}
                      {hasQuickCommands ? (
                        <>
                          {/* Offscreen measure row keeps natural chip widths for overflow math */}
                          <div
                            ref={measureRowRef}
                            aria-hidden
                            className="pointer-events-none absolute top-0 left-0 -z-10 flex w-max gap-1.5 opacity-0"
                          >
                            {quickCommands.map((cmd) => (
                              <span
                                key={cmd.key}
                                className="shrink-0 rounded-lg border border-ui-border bg-ui-surface px-2 py-1 text-[11px] whitespace-nowrap"
                              >
                                {t(quickCommandLabelKey(cmd))}
                              </span>
                            ))}
                          </div>
                          {quickCommands.slice(0, visibleQuickCount).map((cmd) => {
                            const extractChipBusy =
                              (cmd.key === "extractCharacters" || cmd.key === "extractLocations") &&
                              (streaming || Boolean(autoSendMessage?.trim()))
                            return (
                              <button
                                key={cmd.key}
                                type="button"
                                disabled={extractChipBusy}
                                className={cn(
                                  "shrink-0 rounded-lg border border-ui-border bg-ui-surface px-2 py-1 text-[11px] whitespace-nowrap text-ui-text-secondary transition-colors hover:text-ui-text-primary",
                                  extractChipBusy && "pointer-events-none opacity-50",
                                )}
                                onClick={() => {
                                  onQuickCommandUsed?.()
                                  if (cmd.key === "extractCharacters" && onQuickExtractCharacters) {
                                    onQuickExtractCharacters()
                                    return
                                  }
                                  if (cmd.key === "extractLocations" && onQuickExtractLocations) {
                                    onQuickExtractLocations()
                                    return
                                  }
                                  setInput(t(cmd.phraseKey))
                                }}
                              >
                                {t(quickCommandLabelKey(cmd))}
                              </button>
                            )
                          })}
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
            {bodySlot ? (
              <div
                className={cn(
                  "absolute inset-0 transition-opacity duration-200",
                  bodySlotVisible ? "opacity-100" : "pointer-events-none opacity-0",
                )}
                aria-hidden={!bodySlotVisible || undefined}
                {...(!bodySlotVisible ? { inert: true } : {})}
              >
                {bodySlot}
              </div>
            ) : null}
          </div>

          <div className={cn("relative w-full min-w-0 shrink-0", (composerEdgeOverlay || composerSlot) && "mt-3")}>
            <div
              ref={composerBoxRef}
              className={cn(
                "w-full min-w-0",
                !pinComposerSlot && "transition-[height] duration-200 ease-out",
                // Pill uses composerEdgeOverlay outside this box; keep overflow-hidden for height swap.
                composerResizing ? "overflow-hidden" : "overflow-visible",
              )}
              onTransitionEnd={onComposerResizeEnd}
            >
              <div
                ref={composerContentRef}
                className={cn("w-full min-w-0", pinSlot && "grid grid-cols-[minmax(0,1fr)]")}
              >
                {pinSlot || !composerSlot ? (
                  <div
                    className={cn(
                      "min-w-0",
                      pinSlot && "col-start-1 row-start-1 h-full",
                      slotVisible && "pointer-events-none invisible",
                    )}
                    {...(slotVisible ? { inert: true } : {})}
                  >
                    <form
                      className={cn(
                        "relative w-full min-w-0 cursor-text overflow-hidden rounded-lg border border-ui-border bg-ui-surface-sunken",
                        pinSlot && "flex h-full flex-col",
                      )}
                      onMouseDown={(e) => {
                        if (composerLocked) return
                        if (!(e.target instanceof Element)) return
                        if (e.target.closest("button, a, input, [role='listbox'], [role='menu']")) return
                        if (e.target.closest(".ProseMirror")) return
                        e.preventDefault()
                        composerRef.current?.focus()
                      }}
                      onSubmit={(e) => {
                        e.preventDefault()
                        if (!canEditContent) return
                        requestSend(composerRef.current?.getText() ?? input)
                      }}
                    >
                      {!canEditContent ? (
                        <p
                          className={cn("px-2.5 text-[11px] text-ui-text-muted", composerPillOverlap ? "pt-5" : "pt-2")}
                        >
                          {t("agent.readOnlyHint")}
                        </p>
                      ) : null}
                      {editingMessageId != null ? (
                        <div
                          className={cn(
                            "flex items-center gap-2 px-2.5 text-[11px] text-ui-text-secondary",
                            composerPillOverlap && canEditContent ? "pt-5" : "pt-2",
                          )}
                        >
                          <span>{t("agent.editingBanner")}</span>
                          <button
                            type="button"
                            className="ml-auto text-[11px] hover:text-ui-text-primary"
                            onClick={() => {
                              setEditingMessageId(null)
                              composerRef.current?.clear()
                            }}
                          >
                            {t("agent.editCancel")}
                          </button>
                        </div>
                      ) : null}
                      {queuedMessage ? (
                        <div
                          className={cn(
                            "flex w-full min-w-0 items-center gap-1.5 overflow-hidden px-3 text-[11px] text-ui-text-secondary",
                            composerPillOverlap && canEditContent && editingMessageId == null ? "pt-5" : "pt-2",
                          )}
                        >
                          <span className="min-w-0 flex-1 truncate">
                            {t("agent.queuedNext", { text: queuedMessage })}
                          </span>
                          <button
                            type="button"
                            className="ml-auto shrink-0 rounded p-0.5 hover:text-ui-text-primary"
                            aria-label={t("agent.queuedClear")}
                            onClick={clearQueuedMessage}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ) : null}
                      <AgentPendingImageChips
                        pending={pendingImages}
                        onRemove={(key) => {
                          const index = pendingImages.findIndex((item) => item.key === key)
                          const image = index >= 0 ? pendingImages[index] : undefined
                          if (image) releasePendingAgentImage(image)
                          if (editingMessageId == null && index >= 0) {
                            const current = composerRef.current?.getText() ?? input
                            const next = remapAgentImageTokens(current, index + 1)
                            if (next !== current) setInput(next)
                          }
                          setPendingImages((prev) => prev.filter((item) => item.key !== key))
                        }}
                      />
                      <AgentComposerEditor
                        ref={composerRef}
                        value={input}
                        onChange={setInput}
                        onSubmit={() => requestSend(composerRef.current?.getText() ?? input)}
                        onFocus={() => setExpanded(true)}
                        placeholder={t("agent.placeholder")}
                        disabled={composerLocked}
                        expanded={expanded}
                        edgeClearance={composerPillOverlap && !composerTopBanner}
                        skills={availableSkills}
                        onSkillPicked={handleSelectSessionSkill}
                        nodeMentionItems={nodeMentionItems}
                        imageMentionItems={imageMentionItems}
                        className={cn(
                          pinSlot && "min-h-0 flex-1",
                          !showSkillsPicker && (expanded ? "[&_.ProseMirror]:pb-10" : "[&_.ProseMirror]:pr-36"),
                        )}
                      />
                      {showSkillsPicker ? (
                        <div className="agent-skills-row-enter flex shrink-0 items-center px-1 pr-10 pb-1">
                          <AgentSessionSkillsPicker
                            availableSkills={availableSkills}
                            composerText={input}
                            getComposerText={() => composerRef.current?.getText() ?? input}
                            disabled={composerLocked}
                            onSelect={handleSelectSessionSkill}
                          />
                        </div>
                      ) : null}
                      <div
                        className={cn(
                          "absolute right-1 flex items-center gap-3",
                          expanded ? "bottom-1" : "top-1/2 -translate-y-1/2",
                        )}
                      >
                        <AgentImageAttach
                          pending={pendingImages}
                          onChange={setPendingImages}
                          disabled={composerLocked || !imagesReady}
                          projectId={libraryProjectId}
                          frameFormat={frameFormat}
                        />
                        <VoiceDictationButton
                          projectId={projectId}
                          agentScope={resolvedAgentScope}
                          disabled={composerLocked}
                          onText={(text) => composerRef.current?.insertText(text)}
                        />
                        <Button
                          type="submit"
                          size="sm"
                          className="h-8 w-8 shrink-0 p-0"
                          disabled={composerLocked || (!input.trim() && pendingImages.length === 0)}
                        >
                          {composerLocked ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </Button>
                      </div>
                    </form>
                  </div>
                ) : null}
                {composerSlot ? (
                  <div
                    className={cn(
                      pinSlot && "col-start-1 row-start-1 h-full",
                      pinSlot && !composerSlotVisible && "pointer-events-none invisible",
                    )}
                    {...(pinSlot && !composerSlotVisible ? { inert: true } : {})}
                  >
                    {composerSlot}
                  </div>
                ) : null}
              </div>
            </div>
            {composerEdgeOverlay ? (
              composerEdgeOverlayFillWidth ? (
                <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex -translate-y-1/2 justify-center px-2">
                  <div className="pointer-events-auto flex w-full max-w-full justify-center">{composerEdgeOverlay}</div>
                </div>
              ) : (
                <div className="absolute top-0 left-1/2 z-20 -translate-x-1/2 -translate-y-1/2">
                  {composerEdgeOverlay}
                </div>
              )
            ) : null}
          </div>
        </div>
        <TaskFileDropOverlay
          enabled={imageAttachEnabled}
          boundsRef={panelRef}
          label={t("agent.dropOverlay")}
          onDrop={acceptAgentImageFiles}
        />
      </div>
      <ConfirmActionDialog
        open={queueConfirm != null}
        onOpenChange={(open) => {
          if (!open) setQueueConfirm(null)
        }}
        title={queueConfirm?.replace ? t("agent.queueReplaceTitle") : t("agent.queueConfirmTitle")}
        message={queueConfirm?.replace ? t("agent.queueReplaceMessage") : t("agent.queueConfirmMessage")}
        confirmLabel={queueConfirm?.replace ? t("agent.queueReplace") : t("agent.queueConfirm")}
        onConfirm={confirmQueue}
      />
      <ConfirmActionDialog
        open={editConfirmOpen}
        onOpenChange={setEditConfirmOpen}
        title={t("agent.editConfirmTitle")}
        message={t("agent.editConfirmMessage")}
        confirmLabel={t("agent.editConfirm")}
        onConfirm={() => void submitEditedMessage()}
      />
    </div>
  )
}
